import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  ArrowLeft,
  Calendar,
  User,
  Users,
  CreditCard,
  DollarSign,
  Plus,
  FileText,
  EyeOff,
  ShieldAlert,
  Download,
  CheckCircle2,
  Zap,
  Receipt,
  Loader2,
  Trash2,
  Mail,
  AlertCircle,
  Clock,
  Activity,
  MessageSquare,
  Send,
  Pencil,
  BanknoteIcon,
  Banknote,
  Copy,
  Link2,
  Wrench,
  BellOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useT, useLang } from '@/i18n';

const LOCALE_MAP = { en: 'en-GB', es: 'es-ES', fi: 'fi-FI', sv: 'sv-SE', de: 'de-DE' };

const BookingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useT();
  const [lang] = useLang();
  const loc = LOCALE_MAP[lang] || 'en-GB';
  const [booking, setBooking] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [voidingPaymentId, setVoidingPaymentId] = useState(null);
  const [cancellingInvoiceId, setCancellingInvoiceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contractInfo, setContractInfo] = useState(null);
  const [downloadingContract, setDownloadingContract] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  
  // Electricity tracking state
  const [checkInKwh, setCheckInKwh] = useState('');
  const [checkOutKwh, setCheckOutKwh] = useState('');
  const [savingElectricity, setSavingElectricity] = useState(false);
  const [savingCardFee, setSavingCardFee] = useState(false);

  // Admin discount editor
  const [discountForm, setDiscountForm] = useState({ discount_type: 'amount', discount_value: '', discount_reason: '' });
  const [savingDiscount, setSavingDiscount] = useState(false);

  // Primary-guest edit state
  const [editGuestOpen, setEditGuestOpen] = useState(false);
  const [guestForm, setGuestForm] = useState({});
  const [savingGuest, setSavingGuest] = useState(false);

  // Guest refund-notice state
  const [refundPreview, setRefundPreview] = useState(null);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [sendingRefund, setSendingRefund] = useState(false);
  const [refundDeductions, setRefundDeductions] = useState([]);   // [{description, amount}] damage/other

  // Owner-invoice edit state (edit an unpaid invoice to add a missing item)
  const [editInvoiceOpen, setEditInvoiceOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editAdjustments, setEditAdjustments] = useState([]);     // [{description, amount, type}]
  const [editNotes, setEditNotes] = useState('');
  const [newEditAdj, setNewEditAdj] = useState({ description: '', amount: '', type: 'charge' });
  const [savingInvoiceEdit, setSavingInvoiceEdit] = useState(false);
  
  // Owner invoice state
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [ownerInvoice, setOwnerInvoice] = useState(null);
  const [payoutPreview, setPayoutPreview] = useState(null);
  const [loadingPayout, setLoadingPayout] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [newAdjustment, setNewAdjustment] = useState({ description: '', amount: '', type: 'deduction' });
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [ownerInvoices, setOwnerInvoices] = useState([]); // For multiple monthly invoices
  const [monthlyInvoiceStatus, setMonthlyInvoiceStatus] = useState(null); // Status of each month
  const [selectedMonth, setSelectedMonth] = useState(null); // Month being invoiced
  const [monthElectricity, setMonthElectricity] = useState({ start_kwh: '', end_kwh: '' }); // Current month readings
  const [monthlyAdjustments, setMonthlyAdjustments] = useState([]); // Adjustments for current month
  const [newMonthlyAdjustment, setNewMonthlyAdjustment] = useState({ description: '', amount: '', type: 'deduction' });
  
  // Recorded charges — extra costs logged on the day they happen, picked up by
  // the invoice covering that date (see backend booking_charge_service.py)
  const [bookingCharges, setBookingCharges] = useState([]);
  const [chargeTotals, setChargeTotals] = useState(null);
  const [newCharge, setNewCharge] = useState({ date: '', description: '', amount: '', type: 'deduction' });
  const [savingCharge, setSavingCharge] = useState(false);
  const [voidingChargeId, setVoidingChargeId] = useState(null);

  // Payment reminder state
  const [sendingReminder, setSendingReminder] = useState(false);

  // Contract generation state
  const [generatingContract, setGeneratingContract] = useState(false);

  // Confirm-status dialog (PENDING → CONFIRMED asks about deposit)
  const [pendingStatusChange, setPendingStatusChange]     = useState(null);
  const [confirmDialogOpen,   setConfirmDialogOpen]       = useState(false);
  const [depositRecorded,     setDepositRecorded]         = useState('no');
  const [depositForm,         setDepositForm]             = useState({ amount: '', type: 'BANK_TRANSFER', notes: '' });
  const [confirmingStatus,    setConfirmingStatus]        = useState(false);

  // Payment-status-change dialog (records payment when moving to PAID statuses)
  const [payStatusDialogOpen, setPayStatusDialogOpen]     = useState(false);
  const [pendingPayStatus,    setPendingPayStatus]        = useState(null);
  const [payStatusForm,       setPayStatusForm]           = useState({ amount: '', type: 'BANK_TRANSFER', notes: '' });
  const [savingPayStatus,     setSavingPayStatus]         = useState(false);

  // Activity log
  const [activityLog,    setActivityLog]    = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [noteText,       setNoteText]       = useState('');
  const [addingNote,     setAddingNote]     = useState(false);
  
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    type: 'BANK_TRANSFER',
    description: '',
    notes: '',
    month_number: '',
    meter_reading: ''
  });

  const [pausingReminders, setPausingReminders] = useState(false);

  // The booking's monthly billing schedule — every month, not just the ones
  // that have had a payment link generated, so a bank transfer can be put
  // against month 1 or against a month nobody has been reminded about yet.
  const [monthlySchedule, setMonthlySchedule] = useState([]);

  const isAdminOrCS = ['ADMIN', 'CUSTOMER_SERVICE'].includes(user?.role);
  const isOwner = user?.role === 'OWNER';
  const isAdmin = user?.role === 'ADMIN';
  const isCustomer = user?.role === 'CUSTOMER';

  // Edit-dates dialog state
  const [editDatesOpen, setEditDatesOpen] = useState(false);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [savingDates, setSavingDates] = useState(false);

  // Date-edit permission: admin/CS any time; owner any time on own; customer only PENDING.
  // Owner-ownership and customer-ownership are enforced on the backend; here we only
  // hide/show the button.
  const canEditDates = booking && booking.status !== 'CANCELLED' && (
    isAdminOrCS ||
    isOwner ||
    (isCustomer && booking.status === 'PENDING')
  );

  const openEditDates = () => {
    if (!booking) return;
    setEditStartDate(booking.start_date?.slice(0, 10) || '');
    setEditEndDate(booking.end_date?.slice(0, 10) || '');
    setEditDatesOpen(true);
  };

  const submitEditDates = async (e) => {
    e?.preventDefault?.();
    if (!editStartDate || !editEndDate) return;
    if (editStartDate === booking.start_date?.slice(0, 10) && editEndDate === booking.end_date?.slice(0, 10)) {
      toast.error(t('booking_detail.edit_dates.same_as_current') || 'New dates are the same as current dates');
      return;
    }
    setSavingDates(true);
    try {
      const res = await axios.put(`${API}/bookings/${id}/dates`, {
        start_date: editStartDate,
        end_date: editEndDate,
      });
      const summary = res.data?.summary || (t('booking_detail.edit_dates.updated') || 'Dates updated');
      toast.success(summary);
      setEditDatesOpen(false);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        const conflicts = detail?.conflicts || [];
        const refs = conflicts
          .map((c) => c.reference || c.summary || `${c.start_date}→${c.end_date}`)
          .join(', ');
        toast.error(
          (t('booking_detail.edit_dates.unavailable') || 'Dates unavailable') +
          (refs ? ` — ${refs}` : '')
        );
      } else {
        toast.error(
          (typeof detail === 'string' && detail) ||
          (t('booking_detail.edit_dates.failed') || 'Failed to update dates')
        );
      }
    } finally {
      setSavingDates(false);
    }
  };
  
  // Calculate booking nights
  const bookingNights = booking ? Math.ceil((new Date(booking.end_date) - new Date(booking.start_date)) / (1000 * 60 * 60 * 24)) : 0;
  const isLongTermBooking = bookingNights >= 60;

  // Calculate monthly periods for long-term bookings
  const calculateMonthlyPeriods = () => {
    if (!booking || bookingNights < 60) return [];
    
    const periods = [];
    const startDate = new Date(booking.start_date);
    const endDate = new Date(booking.end_date);
    let currentStart = new Date(startDate);
    let monthNum = 1;
    
    while (currentStart < endDate) {
      const periodEnd = new Date(currentStart);
      periodEnd.setMonth(periodEnd.getMonth() + 1);
      const actualEnd = periodEnd > endDate ? endDate : periodEnd;
      const nights = Math.ceil((actualEnd - currentStart) / (1000 * 60 * 60 * 24));
      
      periods.push({
        month: monthNum,
        startDate: currentStart.toISOString().split('T')[0],
        endDate: actualEnd.toISOString().split('T')[0],
        nights: nights,
        start_kwh: '',
        end_kwh: ''
      });
      
      currentStart = new Date(actualEnd);
      monthNum++;
    }
    return periods;
  };
  
  const monthlyPeriods = calculateMonthlyPeriods();

  useEffect(() => {
    fetchData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRecordCharge = async () => {
    const amount = parseFloat(newCharge.amount);
    if (!newCharge.date || !newCharge.description.trim() || !(amount > 0)) {
      toast.error(t('booking_detail.charges.incomplete'));
      return;
    }
    setSavingCharge(true);
    try {
      await axios.post(`${API}/booking-charges`, {
        booking_id: id,
        date: newCharge.date,
        description: newCharge.description.trim(),
        amount,
        type: newCharge.type,
      });
      setNewCharge({ date: '', description: '', amount: '', type: 'deduction' });
      // The month preview is now stale — it is computed with these charges in it.
      setMonthlyPreview(null);
      setPayoutPreview(null);
      await fetchBookingCharges();
      fetchActivityLog();
      toast.success(t('booking_detail.charges.recorded'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('booking_detail.charges.record_failed'));
    } finally {
      setSavingCharge(false);
    }
  };

  const handleVoidCharge = async (charge) => {
    const reason = window.prompt(t('booking_detail.charges.void_prompt', { description: charge.description }));
    if (reason === null) return;   // cancelled the prompt
    setVoidingChargeId(charge.id);
    try {
      await axios.post(`${API}/booking-charges/${charge.id}/void`, { reason });
      setMonthlyPreview(null);
      setPayoutPreview(null);
      await fetchBookingCharges();
      fetchActivityLog();
      toast.success(t('booking_detail.charges.voided'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('booking_detail.charges.void_failed'));
    } finally {
      setVoidingChargeId(null);
    }
  };

  const fetchActivityLog = async () => {
    if (!isAdminOrCS) return;
    setActivityLoading(true);
    try {
      const res = await axios.get(`${API}/bookings/${id}/activity`);
      setActivityLog(res.data.events || []);
    } catch {
      // silently fail — non-critical
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchMonthlySchedule = async () => {
    if (!isAdminOrCS) return;
    try {
      const res = await axios.get(`${API}/monthly-payments/booking/${id}`);
      setMonthlySchedule(res.data.schedule || []);
    } catch {
      setMonthlySchedule([]);
    }
  };

  const fetchBookingCharges = async () => {
    if (!isAdminOrCS) return;
    try {
      const res = await axios.get(`${API}/booking-charges/booking/${id}`);
      setBookingCharges(res.data.items || []);
      setChargeTotals(res.data.totals || null);
    } catch {
      setBookingCharges([]);
      setChargeTotals(null);
    }
  };

  const fetchData = async () => {
    try {
      const bookingRes = await axios.get(`${API}/bookings/${id}`);
      setBooking(bookingRes.data);
      
      // Set electricity values from booking
      if (bookingRes.data.check_in_kwh !== undefined) {
        setCheckInKwh(bookingRes.data.check_in_kwh?.toString() || '');
      }
      if (bookingRes.data.check_out_kwh !== undefined) {
        setCheckOutKwh(bookingRes.data.check_out_kwh?.toString() || '');
      }
      
      // Fetch contract info for this booking
      try {
        const contractRes = await axios.get(`${API}/contracts/booking/${id}`);
        if (contractRes.data?.has_contract) {
          setContractInfo(contractRes.data.contract);
        }
      } catch (err) {
        // No contract for this booking
        setContractInfo(null);
      }
      
      // Fetch owner invoice if exists
      if (isAdminOrCS) {
        try {
          const invoiceRes = await axios.get(`${API}/owner-invoices/booking/${id}`);
          if (invoiceRes.data?.has_invoices) {
            setOwnerInvoices(invoiceRes.data.invoices || []);
            setOwnerInvoice(invoiceRes.data.invoices?.[0] || null);
          } else if (invoiceRes.data?.has_invoice) {
            // Backward compatibility
            setOwnerInvoice(invoiceRes.data.invoice);
            setOwnerInvoices(invoiceRes.data.invoice ? [invoiceRes.data.invoice] : []);
          }
        } catch (err) {
          // No invoice yet
          setOwnerInvoice(null);
          setOwnerInvoices([]);
        }
        
        // Fetch monthly invoice status for long-term bookings
        try {
          const statusRes = await axios.get(`${API}/owner-invoices/booking/${id}/monthly-status`);
          setMonthlyInvoiceStatus(statusRes.data);
        } catch (err) {
          setMonthlyInvoiceStatus(null);
        }

        await fetchBookingCharges();
        fetchMonthlySchedule();
      }
      
      // Only fetch payments if user has access (not OWNER)
      if (!isOwner) {
        try {
          const paymentsRes = await axios.get(`${API}/payments/${id}/records`);
          setPayments(paymentsRes.data || []);
        } catch (err) {
          // OWNER gets 403, which is expected
          if (err.response?.status !== 403) {
            console.error('Payment fetch error:', err);
          }
          setPayments([]);
        }
        // Payment summary (total paid / balance remaining) — the source of truth
        try {
          const summaryRes = await axios.get(`${API}/payments/${id}/summary`);
          setPaymentSummary(summaryRes.data || null);
        } catch (err) {
          setPaymentSummary(null);
        }
      }
    } catch (error) {
      toast.error(t('booking_detail.toasts.load_failed'));
      navigate('/bookings');
    } finally {
      setLoading(false);
    }
    // Fetch activity log separately (non-blocking)
    if (isAdminOrCS) fetchActivityLog();
  };

  // Save electricity readings
  const handleSaveElectricity = async () => {
    setSavingElectricity(true);
    try {
      const data = {};
      if (checkInKwh !== '') data.check_in_kwh = parseFloat(checkInKwh);
      if (checkOutKwh !== '') data.check_out_kwh = parseFloat(checkOutKwh);
      
      const response = await axios.put(`${API}/owner-invoices/bookings/${id}/electricity`, data);
      setBooking(response.data);
      toast.success(t('booking_detail.toasts.elec_saved'));
    } catch (error) {
      toast.error(t('booking_detail.toasts.elec_failed'));
    } finally {
      setSavingElectricity(false);
    }
  };

  // How the monthly card fee is handled on this booking.
  // 'auto' (date-based default) | 'charge' | 'recover' | 'bank_transfer'.
  // bank_transfer is not a third shade of "don't charge the tenant": no card is
  // presented, so there is no fee to charge OR to recover from the owner.
  const handleSetCardFee = async (value) => {
    setSavingCardFee(true);
    try {
      await axios.put(`${API}/bookings/${id}/card-fee`, null, { params: { mode: value } });
      await fetchData();
      toast.success(t('booking_detail.card_fee.saved'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.card_fee.failed'));
    } finally {
      setSavingCardFee(false);
    }
  };

  // Apply or remove an admin discount on the booking total
  const handleSaveDiscount = async (clear = false) => {
    setSavingDiscount(true);
    try {
      const body = clear
        ? { discount_type: null, discount_value: 0 }
        : {
            discount_type: discountForm.discount_type,
            discount_value: parseFloat(discountForm.discount_value) || 0,
            discount_reason: discountForm.discount_reason || null,
          };
      const res = await axios.put(`${API}/bookings/${id}/discount`, body);
      await fetchData();
      if (clear) {
        setDiscountForm({ discount_type: 'amount', discount_value: '', discount_reason: '' });
        toast.success(t('booking_detail.toasts.discount_removed'));
      } else {
        toast.success(t('booking_detail.toasts.discount_applied'));
        if (res.data?.credit_due > 0) {
          toast.info(t('booking_detail.toasts.discount_credit', { amount: res.data.credit_due.toFixed(2) }));
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.discount_failed'));
    } finally {
      setSavingDiscount(false);
    }
  };

  // Open the primary-guest edit dialog, pre-filled from current values
  const openEditGuest = (guest) => {
    setGuestForm({
      first_name: guest.first_name || '',
      last_name: guest.last_name || '',
      email: guest.email || '',
      phone: guest.phone || '',
      id_type: guest.id_type || '',
      id_number: guest.id_number || '',
      nationality: guest.nationality || '',
      date_of_birth: guest.date_of_birth || '',
    });
    setEditGuestOpen(true);
  };

  // Save edited primary guest
  const handleSaveGuest = async (guestId) => {
    setSavingGuest(true);
    try {
      await axios.put(`${API}/bookings/${id}/guests/${guestId}`, guestForm);
      await fetchData();
      toast.success(t('booking_detail.guest.save_success'));
      setEditGuestOpen(false);
    } catch (error) {
      toast.error(t('booking_detail.guest.save_error'));
    } finally {
      setSavingGuest(false);
    }
  };

  // Deductions off the guest refund (damage / broken items / other)
  const refundDeductionsPayload = () => ({
    deductions: refundDeductions
      .filter(d => d.description && parseFloat(d.amount) > 0)
      .map(d => ({ description: d.description, amount: parseFloat(d.amount) })),
  });

  // Preview the guest refund (deposit - electricity - deductions), no email sent
  const handlePreviewRefund = async () => {
    setLoadingRefund(true);
    try {
      const response = await axios.post(`${API}/bookings/${id}/refund-notice?preview=true`, refundDeductionsPayload());
      setRefundPreview(response.data.breakdown);
    } catch (error) {
      toast.error(t('booking_detail.refund.send_error'));
    } finally {
      setLoadingRefund(false);
    }
  };

  // Email the refund notice to the guest
  const handleSendRefundNotice = async () => {
    setSendingRefund(true);
    try {
      const response = await axios.post(`${API}/bookings/${id}/refund-notice`, refundDeductionsPayload());
      setRefundPreview(response.data.breakdown);
      toast.success(t('booking_detail.refund.sent_success'));
    } catch (error) {
      toast.error(t('booking_detail.refund.send_error'));
    } finally {
      setSendingRefund(false);
    }
  };

  const addRefundDeduction = () => setRefundDeductions(prev => [...prev, { description: '', amount: '' }]);
  const updateRefundDeduction = (i, field, val) =>
    setRefundDeductions(prev => prev.map((d, idx) => idx === i ? { ...d, [field]: val } : d));
  const removeRefundDeduction = (i) =>
    setRefundDeductions(prev => prev.filter((_, idx) => idx !== i));

  // Calculate payout preview
  const handleCalculatePayout = async () => {
    setLoadingPayout(true);
    try {
      const response = await axios.post(`${API}/owner-invoices/calculate-payout`, {
        booking_id: id,
        adjustments: adjustments.map(a => ({
          description: a.description,
          amount: parseFloat(a.amount),
          type: a.type
        }))
      });
      setPayoutPreview(response.data);
    } catch (error) {
      toast.error(t('booking_detail.toasts.payout_failed'));
    } finally {
      setLoadingPayout(false);
    }
  };

  // Add adjustment
  const handleAddAdjustment = () => {
    if (!newAdjustment.description || !newAdjustment.amount) {
      toast.error(t('booking_detail.toasts.fill_desc_amount'));
      return;
    }
    setAdjustments([...adjustments, { ...newAdjustment, id: Date.now() }]);
    setNewAdjustment({ description: '', amount: '', type: 'deduction' });
  };

  // Remove adjustment
  const handleRemoveAdjustment = (adjustmentId) => {
    setAdjustments(adjustments.filter(a => a.id !== adjustmentId));
  };

  // Regenerate a monthly payment link for a specific month (months 2+).
  // Month 1 goes through the balance flow — see handleRequestBalance.
  // The fresh link is shown inline (copyable) and can be emailed to the customer.
  const [regeneratingToken, setRegeneratingToken] = useState(null);
  const [regeneratedLinks, setRegeneratedLinks] = useState({});
  const handleRegeneratePaymentLink = async (monthNumber, sendEmail = false) => {
    setRegeneratingToken(monthNumber);
    try {
      const response = await axios.post(`${API}/monthly-payments/regenerate-token`, {
        booking_id: id,
        month_number: monthNumber,
        send_email: sendEmail,
      });
      setRegeneratedLinks(prev => ({ ...prev, [monthNumber]: response.data.payment_url }));
      if (sendEmail) {
        toast.success(t('booking_detail.invoice.link_emailed', { n: monthNumber, email: response.data.recipient_email }));
      } else {
        toast.success(t('booking_detail.invoice.link_ready', { n: monthNumber }));
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.invoice.link_failed'));
    } finally {
      setRegeneratingToken(null);
    }
  };
  const copyPaymentLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('booking_detail.invoice.copied'));
    } catch {
      toast.error(url);
    }
  };

  // Send the balance / month-1 rent request now instead of waiting for the
  // daily job. Admin + Customer Service. Backend moves the booking to
  // BALANCE_DUE so the scheduled request is not sent a second time.
  const [requestingBalance, setRequestingBalance] = useState(false);
  const handleRequestBalance = async () => {
    setRequestingBalance(true);
    try {
      const response = await axios.post(`${API}/payments/request-balance/${id}`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.reminder.request_failed'));
    } finally {
      setRequestingBalance(false);
    }
  };

  // Open a month's generate form with the readings already on record.
  // The start reading is never a number anyone should be typing: month 1
  // measures from the check-in reading and every later month from where the
  // one before it ended, and a blank field invited a 0 — which bills the
  // tenant's whole meter to the owner. The end reading is filled only when the
  // tenant has already paid the month and said what the meter read; otherwise
  // it is the admin's to take.
  const openMonthForInvoice = (month) => {
    const elec = month.electricity || {};
    setMonthElectricity({
      start_kwh: elec.start_kwh != null ? String(elec.start_kwh) : '',
      end_kwh:   elec.end_kwh   != null ? String(elec.end_kwh)   : '',
    });
    setMonthlyAdjustments([]);
    setNewMonthlyAdjustment({ description: '', amount: '', type: 'deduction' });
    setMonthlyPreview(null);
    setSelectedMonth(month.month);
  };

  // Preview one month before it becomes a numbered invoice. Same endpoint data
  // the generator uses, so what's shown here is what gets written. Cleared
  // whenever a reading or adjustment changes, so a stale total can't be read as
  // current.
  const [monthlyPreview, setMonthlyPreview] = useState(null);
  const [loadingMonthlyPreview, setLoadingMonthlyPreview] = useState(false);
  const handleCalculateMonthlyPayout = async (monthNumber) => {
    setLoadingMonthlyPreview(true);
    try {
      const response = await axios.post(`${API}/owner-invoices/calculate-monthly-payout`, {
        booking_id: id,
        month_number: monthNumber,
        electricity_start_kwh: parseFloat(monthElectricity.start_kwh) || 0,
        electricity_end_kwh: parseFloat(monthElectricity.end_kwh) || 0,
        adjustments: monthlyAdjustments.map(a => ({
          description: a.description,
          amount: parseFloat(a.amount),
          type: a.type
        }))
      });
      setMonthlyPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.payout_failed'));
    } finally {
      setLoadingMonthlyPreview(false);
    }
  };

  // Generate a single monthly invoice for a specific month
  const handleGenerateSingleMonthInvoice = async (monthNumber) => {
    setGeneratingInvoice(true);
    try {
      const response = await axios.post(`${API}/owner-invoices/generate-single-month`, {
        booking_id: id,
        month_number: monthNumber,
        electricity_start_kwh: parseFloat(monthElectricity.start_kwh) || 0,
        electricity_end_kwh: parseFloat(monthElectricity.end_kwh) || 0,
        adjustments: monthlyAdjustments.map(a => ({
          description: a.description,
          amount: parseFloat(a.amount),
          type: a.type
        })),
        notes: invoiceNotes || undefined
      });
      
      toast.success(t('booking_detail.toasts.invoice_generated', { number: response.data.invoice_number, month: monthNumber }));
      setSelectedMonth(null);
      setMonthElectricity({ start_kwh: '', end_kwh: '' });
      setMonthlyAdjustments([]);
      setNewMonthlyAdjustment({ description: '', amount: '', type: 'deduction' });
      setMonthlyPreview(null);
      setShowInvoiceDialog(false);
      fetchData();  // Refresh to show new invoice
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.invoice_failed'));
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Generate owner invoice (single invoice for short-term bookings)
  const handleGenerateInvoice = async () => {
    setGeneratingInvoice(true);
    try {
      // For short-term bookings, generate single invoice
      const response = await axios.post(`${API}/owner-invoices/generate`, {
        booking_id: id,
        adjustments: adjustments.map(a => ({
          description: a.description,
          amount: parseFloat(a.amount),
          type: a.type
        })),
        notes: invoiceNotes || undefined
      });
      setOwnerInvoice(response.data);
      setOwnerInvoices([response.data]);
      toast.success(t('booking_detail.toasts.invoice_generated_ok', { number: response.data.invoice_number }));

      setShowInvoiceDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.invoice_failed'));
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Send payment reminder
  const handleSendPaymentReminder = async () => {
    setSendingReminder(true);
    try {
      const response = await axios.post(`${API}/payments/send-reminder/${id}`);
      toast.success(response.data.message || t('booking_detail.toasts.reminder_sent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.reminder_failed'));
    } finally {
      setSendingReminder(false);
    }
  };

  // Generate contract for manual bookings (when deposit confirmed)
  const handleGenerateContract = async () => {
    setGeneratingContract(true);
    try {
      // Get guest info
      const guests = booking.guests || [];
      const primaryGuest = guests.find(g => g.is_primary) || guests[0] || {};
      
      const response = await axios.post(`${API}/contracts/sign`, {
        booking_id: id,
        signature_name: `${primaryGuest.first_name || ''} ${primaryGuest.last_name || ''}`.trim() || 'Guest',
        signature_type: 'full_name',
        tenant_id_number: primaryGuest.id_number || undefined
      });
      
      setContractInfo(response.data);
      toast.success(t('booking_detail.toasts.contract_generated'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.contract_failed'));
    } finally {
      setGeneratingContract(false);
    }
  };

  // Download owner invoice
  const handleDownloadInvoice = async (invoice = ownerInvoice) => {
    if (!invoice?.id) return;
    
    try {
      const response = await axios.get(`${API}/owner-invoices/${invoice.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('booking_detail.toasts.invoice_downloaded'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('booking_detail.toasts.invoice_dl_failed'));
    }
  };
  
  // Auto-allocate payments to invoices
  const handleAutoAllocatePayments = async () => {
    try {
      const response = await axios.post(`${API}/owner-invoices/auto-allocate`, {
        booking_id: id
      });
      toast.success(response.data.message);
      fetchData();  // Refresh to show updated allocations
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.allocate_failed'));
    }
  };

  // Update invoice status (Mark as Paid / Paid Out)
  const handleUpdateInvoiceStatus = async (newStatus, invoiceId = ownerInvoice?.id) => {
    if (!invoiceId) return;
    
    try {
      await axios.put(`${API}/owner-invoices/${invoiceId}/status`, {
        status: newStatus
      });
      toast.success(t('booking_detail.toasts.invoice_marked', { status: newStatus.replace('_', ' ') }));
      fetchData();  // Refresh data
    } catch (error) {
      toast.error(t('booking_detail.toasts.invoice_status_failed'));
    }
  };

  // Cancel an invoice (admin) — the other half of the preview. An issued
  // invoice carries a sequential tax number, so a wrong figure is never
  // rewritten in place: it is cancelled and re-issued. A CANCELLED invoice no
  // longer blocks its month, so the month becomes generatable again.
  const handleCancelInvoice = async (invoice) => {
    let confirmMsg = t('booking_detail.invoice.cancel_confirm', { number: invoice.invoice_number });
    if (invoice.status === 'PAID_OUT') {
      confirmMsg += `\n\n${t('booking_detail.invoice.cancel_confirm_paid_out')}`;
    }
    if (!window.confirm(confirmMsg)) return;
    setCancellingInvoiceId(invoice.id);
    try {
      await axios.put(`${API}/owner-invoices/${invoice.id}/status`, { status: 'CANCELLED' });
      toast.success(t('booking_detail.toasts.invoice_cancelled', { number: invoice.invoice_number }));
      setMonthlyPreview(null);   // any open preview predates the cancellation
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.invoice_cancel_failed'));
    } finally {
      setCancellingInvoiceId(null);
    }
  };

  // Edit an unpaid owner invoice (add a missing line item, etc.)
  const openEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setEditAdjustments((invoice.adjustments || []).map((a, i) => ({
      id: `${Date.now()}-${i}`,
      description: a.description || '',
      amount: a.amount,
      type: a.type || 'charge',
    })));
    setEditNotes(invoice.notes || '');
    setNewEditAdj({ description: '', amount: '', type: 'charge' });
    setEditInvoiceOpen(true);
  };

  const addEditAdjustment = () => {
    if (!newEditAdj.description || !newEditAdj.amount) {
      toast.error(t('booking_detail.toasts.fill_desc_amount'));
      return;
    }
    setEditAdjustments(prev => [...prev, { ...newEditAdj, id: Date.now() }]);
    setNewEditAdj({ description: '', amount: '', type: 'charge' });
  };

  const handleSaveInvoiceEdit = async () => {
    if (!editingInvoice) return;
    setSavingInvoiceEdit(true);
    try {
      await axios.put(`${API}/owner-invoices/${editingInvoice.id}`, {
        adjustments: editAdjustments.map(a => ({
          description: a.description,
          amount: parseFloat(a.amount),
          type: a.type,
        })),
        notes: editNotes || undefined,
      });
      toast.success(t('booking_detail.invoice.edit_success'));
      setEditInvoiceOpen(false);
      setEditingInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.invoice.edit_failed'));
    } finally {
      setSavingInvoiceEdit(false);
    }
  };

  const handleDownloadContract = async () => {
    if (!contractInfo?.id) return;
    
    setDownloadingContract(true);
    try {
      const response = await axios.get(`${API}/contracts/download/${contractInfo.id}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract_${booking?.reference || id}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('booking_detail.toasts.contract_downloaded'));
    } catch (error) {
      toast.error(t('booking_detail.toasts.contract_dl_failed'));
    } finally {
      setDownloadingContract(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    // Intercept PENDING → CONFIRMED to ask about deposit payment
    if (booking?.status === 'PENDING' && newStatus === 'CONFIRMED') {
      setDepositForm({ amount: '', type: 'BANK_TRANSFER', notes: '' });
      setDepositRecorded('no');
      setPendingStatusChange(newStatus);
      setConfirmDialogOpen(true);
      return;
    }
    // All other status changes proceed directly
    applyStatusChange(newStatus);
  };

  const applyStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/bookings/${id}/status?status=${newStatus}`);
      toast.success(t('booking_detail.toasts.status_updated'));
      fetchData();
    } catch (error) {
      toast.error(t('booking_detail.toasts.status_failed'));
    }
  };

  const handleConfirmStatusWithPayment = async () => {
    setConfirmingStatus(true);
    try {
      // 1. Update booking status
      await axios.put(`${API}/bookings/${id}/status?status=${pendingStatusChange}`);

      // 2. Optionally record deposit payment — recording auto-derives the
      // payment status (no separate status PUT, which used to double-count).
      if (depositRecorded === 'yes' && depositForm.amount) {
        await axios.post(`${API}/payments/manual`, {
          booking_id: id,
          amount: parseFloat(depositForm.amount),
          type: depositForm.type,
          description: 'Booking Deposit',
          notes: depositForm.notes || undefined,
        });
        toast.success(t('booking_detail.toasts.confirmed_with_deposit'));
      } else {
        toast.success(t('booking_detail.toasts.confirmed'));
      }

      setConfirmDialogOpen(false);
      setPendingStatusChange(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.confirm_failed'));
    } finally {
      setConfirmingStatus(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/bookings/${id}/notes`, { note: noteText.trim() });
      setNoteText('');
      fetchActivityLog();
      toast.success(t('booking_detail.toasts.note_added'));
    } catch {
      toast.error(t('booking_detail.toasts.note_failed'));
    } finally {
      setAddingNote(false);
    }
  };

  // Payment statuses that should prompt for a payment record
  const PAY_STATUS_NEEDS_RECORD = {
    // Prefer the actual saved deposit_amount (correct for both 20% short-term
    // and 1-month long-term bookings); fall back to 20% of total only if the
    // breakdown is missing the deposit field.
    DEPOSIT_PAID: { label: t('booking_detail.dialog_pay_status.label_deposit_payment'), hint: (b) => t('booking_detail.dialog_pay_status.hint_deposit', { amount: (b?.price_breakdown?.deposit_amount ?? ((b?.price_breakdown?.total_amount || 0) * 0.2)).toFixed(2) }) },
    BALANCE_PAID: { label: t('booking_detail.dialog_pay_status.label_balance_payment'), hint: (b) => t('booking_detail.dialog_pay_status.hint_balance', { amount: Math.max(0, (b?.price_breakdown?.total_amount || 0) - (b?.price_breakdown?.deposit_amount || 0)).toFixed(2) }) },
    REFUNDED:     { label: t('booking_detail.dialog_pay_status.label_refund'), hint: () => t('booking_detail.dialog_pay_status.hint_refund') },
  };

  const handlePaymentStatusChange = (newStatus) => {
    if (PAY_STATUS_NEEDS_RECORD[newStatus]) {
      // Pre-fill with what's actually still OWED (never total−deposit, which
      // double-counts an already-recorded balance). Uses the live summary's
      // total_paid so recording only tops up the remainder.
      let defaultAmount = '';
      const total = booking?.price_breakdown?.total_amount || 0;
      const deposit = booking?.price_breakdown?.deposit_amount || (total * 0.2);
      const paid = paymentSummary?.total_paid || 0;
      if (newStatus === 'DEPOSIT_PAID') defaultAmount = Math.max(0, deposit - paid).toFixed(2);
      if (newStatus === 'BALANCE_PAID') defaultAmount = Math.max(0, total - paid).toFixed(2);
      setPayStatusForm({ amount: defaultAmount, type: 'BANK_TRANSFER', notes: '' });
      setPendingPayStatus(newStatus);
      setPayStatusDialogOpen(true);
      return;
    }
    // Statuses with no payment (DEPOSIT_DUE, BALANCE_DUE) apply directly
    applyPaymentStatusChange(newStatus);
  };

  const applyPaymentStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/bookings/${id}/payment-status?payment_status=${newStatus}`);
      toast.success(t('booking_detail.toasts.pay_status_updated'));
      fetchData();
    } catch (error) {
      toast.error(t('booking_detail.toasts.pay_status_failed'));
    }
  };

  const handleConfirmPayStatusWithPayment = async () => {
    setSavingPayStatus(true);
    try {
      const cfg = PAY_STATUS_NEEDS_RECORD[pendingPayStatus];
      if (pendingPayStatus === 'REFUNDED') {
        // Refund is a distinct state, not a positive payment record.
        await axios.put(`${API}/bookings/${id}/payment-status?payment_status=REFUNDED`);
      } else {
        // Record the payment ONLY — the badge auto-derives from recorded
        // payments (source of truth). No separate status PUT, so marking
        // "paid" can't double-count. Amount is pre-filled to the remaining
        // balance, so confirming as-is settles exactly what's owed.
        const amt = Math.abs(parseFloat(payStatusForm.amount) || 0);
        if (amt > 0) {
          await axios.post(`${API}/payments/manual`, {
            booking_id: id,
            amount: amt,
            type: payStatusForm.type,
            description: cfg.label,
            notes: payStatusForm.notes || undefined,
          });
        }
      }
      toast.success(t('booking_detail.toasts.pay_recorded_updated', { label: cfg.label }));
      setPayStatusDialogOpen(false);
      setPendingPayStatus(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.pay_status_failed'));
    } finally {
      setSavingPayStatus(false);
    }
  };

  // Pause or resume the daily job's automatic payment reminders for this one
  // booking. The booking still transitions DEPOSIT_PAID -> BALANCE_DUE while
  // paused, and the manual "Send payment reminder" button still sends.
  const handleToggleReminders = async (nextPaused) => {
    let reason;
    if (nextPaused) {
      reason = window.prompt(t('booking_detail.reminders_pause.reason_prompt'));
      if (reason === null) return;   // cancelled the prompt
    }
    setPausingReminders(true);
    try {
      await axios.put(`${API}/bookings/${id}/reminders`, {
        paused: nextPaused,
        reason: reason || undefined,
      });
      toast.success(nextPaused
        ? t('booking_detail.reminders_pause.paused_toast')
        : t('booking_detail.reminders_pause.resumed_toast'));
      await fetchData();
      fetchActivityLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('booking_detail.reminders_pause.failed'));
    } finally {
      setPausingReminders(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments/manual`, {
        booking_id: id,
        amount: paymentForm.amount,
        type: paymentForm.type,
        description: paymentForm.description,
        notes: paymentForm.notes || undefined,
        // Attributing the payment to a month marks that month paid and stops its
        // reminders. It writes nothing to the owner's invoices — recording a
        // payment is bookkeeping, and the owner invoice is generated separately.
        month_number: paymentForm.month_number ? parseInt(paymentForm.month_number, 10) : undefined,
        meter_reading: paymentForm.meter_reading ? parseFloat(paymentForm.meter_reading) : undefined
      });
      toast.success(t('booking_detail.toasts.payment_recorded'));
      setShowPaymentDialog(false);
      setPaymentForm({ amount: 0, type: 'BANK_TRANSFER', description: '', notes: '', month_number: '', meter_reading: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.payment_failed'));
    }
  };

  // Void a payment (admin) — corrects a mistake such as a duplicate; keeps the
  // row for the audit trail and recomputes the balance/status.
  const handleVoidPayment = async (payment) => {
    if (!window.confirm(t('booking_detail.payments.void_confirm', { amount: payment.amount?.toLocaleString(loc) }))) return;
    const reason = window.prompt(t('booking_detail.payments.void_reason_prompt')) || undefined;
    setVoidingPaymentId(payment.id);
    try {
      await axios.post(`${API}/payments/${payment.id}/void`, null, { params: reason ? { reason } : {} });
      toast.success(t('booking_detail.payments.void_success'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.payments.void_failed'));
    } finally {
      setVoidingPaymentId(null);
    }
  };

  const handleDeleteBooking = async () => {
    const ref = booking?.reference || id;
    const confirmMsg =
      `Delete booking ${ref}?\n\n` +
      `This permanently removes the booking and ALL related records ` +
      `(guests, payments, payment tokens, email logs, contracts, draft owner invoices).\n\n` +
      `Stripe charges in the Stripe dashboard are NOT refunded.\n\n` +
      `This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await axios.delete(`${API}/bookings/${id}`);
      const counts = res.data?.cascade || {};
      const total = Object.values(counts).reduce((sum, n) => sum + (n || 0), 0);
      toast.success(`Booking ${ref} deleted (${total} related records removed)`);
      navigate('/bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  const handleStripePayment = async (amountType) => {
    try {
      const response = await axios.post(`${API}/stripe/create-checkout`, {
        booking_id: id,
        origin_url: window.location.origin,
        payment_type: amountType,
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking_detail.toasts.stripe_session_failed'));
    }
  };

  const STATUS_LABELS = {
    PENDING: t('booking_detail.status.pending'),
    CONFIRMED: t('booking_detail.status.confirmed'),
    CANCELLED: t('booking_detail.status.cancelled'),
  };
  const PAY_STATUS_LABELS = {
    DEPOSIT_DUE: t('booking_detail.status.deposit_due'),
    DEPOSIT_PAID: t('booking_detail.status.deposit_paid'),
    BALANCE_DUE: t('booking_detail.status.balance_due'),
    BALANCE_PAID: t('booking_detail.status.balance_paid'),
    REFUNDED: t('booking_detail.status.refunded'),
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'bg-amber-100 text-amber-800',
      CONFIRMED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{STATUS_LABELS[status] || status}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const styles = {
      DEPOSIT_DUE: 'bg-amber-100 text-amber-800',
      DEPOSIT_PAID: 'bg-blue-100 text-blue-800',
      BALANCE_DUE: 'bg-orange-100 text-orange-800',
      BALANCE_PAID: 'bg-green-100 text-green-800',
      REFUNDED: 'bg-purple-100 text-purple-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{PAY_STATUS_LABELS[status] || status?.replace(/_/g, ' ')}</Badge>;
  };

  // Get primary guest from guests array
  const getPrimaryGuest = () => {
    if (!booking?.guests || booking.guests.length === 0) return null;
    return booking.guests.find(g => g.is_primary) || booking.guests[0];
  };

  // ── Activity log helpers ──────────────────────────────────────
  const activityConfig = {
    BOOKING_CREATED:               { icon: CheckCircle2, color: 'text-green-600',  bg: 'bg-green-100',  label: t('booking_detail.activity.action_booking_created') },
    BOOKING_STATUS_CHANGED:        { icon: Activity,     color: 'text-blue-600',   bg: 'bg-blue-100',   label: t('booking_detail.activity.action_status_changed') },
    BOOKING_PAYMENT_STATUS_CHANGED:{ icon: CreditCard,   color: 'text-purple-600', bg: 'bg-purple-100', label: t('booking_detail.activity.action_payment_status_changed') },
    PAYMENT_RECORDED:              { icon: Banknote,     color: 'text-green-600',  bg: 'bg-green-100',  label: t('booking_detail.activity.action_payment_recorded') },
    REFUND_RECORDED:               { icon: DollarSign,   color: 'text-red-600',    bg: 'bg-red-100',    label: t('booking_detail.activity.action_refund_recorded') },
    ELECTRICITY_RECORDED:          { icon: Zap,          color: 'text-amber-600',  bg: 'bg-amber-100',  label: t('booking_detail.activity.action_electricity_saved') },
    INVOICE_GENERATED:             { icon: Receipt,      color: 'text-teal-600',   bg: 'bg-teal-100',   label: t('booking_detail.activity.action_invoice_generated') },
    CONTRACT_GENERATED:            { icon: FileText,     color: 'text-indigo-600', bg: 'bg-indigo-100', label: t('booking_detail.activity.action_contract_generated') },
    PAYMENT_REMINDER_SENT:         { icon: Mail,         color: 'text-orange-600', bg: 'bg-orange-100', label: t('booking_detail.activity.action_reminder_sent') },
    NOTE_ADDED:                    { icon: MessageSquare,color: 'text-gray-600',   bg: 'bg-gray-100',   label: t('booking_detail.activity.action_note') },
  };

  const getActivityCfg = (action) =>
    activityConfig[action] || { icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100', label: action?.replace(/_/g, ' ') };

  const fmtTs = (ts) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleString(loc, {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return ts; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!booking) return null;

  const price = booking.price_breakdown || {};
  // Server-derived gross ("bruto") view of the card-processing fee. Display-only
  // — the stored price_breakdown stays net (what the owner is owed), so owner
  // invoices and payouts are unaffected. Absent for OWNER/CUSTOMER roles.
  const cardFee = booking.card_fee_summary || null;
  const cardFeeCharge = (key) => cardFee?.charges?.find((c) => c.key === key) || null;
  const primaryGuest = getPrimaryGuest();
  const additionalGuests = booking.guests?.filter(g => !g.is_primary) || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="booking-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/bookings">
          <Button variant="ghost" size="icon" className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">{booking.reference}</h1>
            {booking.is_owner_reservation && (
              <Badge className="bg-[#d8a28c]/30 text-[#0c2431]">{t('booking_detail.header.owner_reservation')}</Badge>
            )}
          </div>
          <p className="text-[#194a50]/70 mt-1">
            {t('booking_detail.header.created', { date: new Date(booking.created_at).toLocaleDateString(loc) })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(booking.status)}
          {!isOwner && getPaymentBadge(booking.payment_status)}
        </div>
      </div>

      {/* OWNER Privacy Notice */}
      {isOwner && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">{t('booking_detail.header.privacy_title')}</p>
            <p className="text-sm text-amber-700">
              {t('booking_detail.header.privacy_body')}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Booking Info */}
          <Card className="shadow-sm border-[#194a50]/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Outfit']">
                <Calendar className="w-5 h-5 text-[#194a50]" />
                {t('booking_detail.info.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#194a50]/60">{t('booking_detail.info.property')}</p>
                  <Link to={`/properties/${booking.property_id}`} className="font-medium text-[#194a50] hover:underline">
                    {booking.property_title || t('booking_detail.info.view_property')}
                  </Link>
                </div>
                <div>
                  <p className="text-sm text-[#194a50]/60">{t('booking_detail.info.duration')}</p>
                  <p className="font-medium text-[#0c2431]">{t('booking_detail.info.nights_n', { n: booking.nights || price.nights || 0 })}</p>
                </div>
                <div>
                  <p className="text-sm text-[#194a50]/60">{t('booking_detail.info.checkin')}</p>
                  <p className="font-medium text-[#0c2431]">
                    {new Date(booking.start_date).toLocaleDateString(loc, {
                      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#194a50]/60">{t('booking_detail.info.checkout')}</p>
                  <p className="font-medium text-[#0c2431]">
                    {new Date(booking.end_date).toLocaleDateString(loc, {
                      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                    })}
                  </p>
                </div>
                {/* Guest count from the booking form (num_guests on the booking
                    doc). Falls back to the length of the per-guest array for
                    legacy bookings that never populated num_guests. The public
                    form caps the dropdown at 8+, so parties of 9+ show as 8 —
                    see BookingForm.tsx around line 641. */}
                <div>
                  <p className="text-sm text-[#194a50]/60">{t('booking_detail.info.guests')}</p>
                  <p className="font-medium text-[#0c2431]">
                    {booking.num_guests ?? booking.guests?.length ?? 1}
                  </p>
                </div>
              </div>

              <Dialog open={editDatesOpen} onOpenChange={setEditDatesOpen}>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle>{t('booking_detail.edit_dates.title') || 'Edit booking dates'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={submitEditDates} className="space-y-4 mt-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>{t('booking_detail.info.checkin')}</Label>
                        <Input
                          type="date"
                          value={editStartDate}
                          onChange={(e) => setEditStartDate(e.target.value)}
                          required
                          data-testid="edit-start-date-input"
                        />
                      </div>
                      <div>
                        <Label>{t('booking_detail.info.checkout')}</Label>
                        <Input
                          type="date"
                          value={editEndDate}
                          onChange={(e) => setEditEndDate(e.target.value)}
                          required
                          data-testid="edit-end-date-input"
                        />
                      </div>
                    </div>
                    <div className="rounded-lg border border-[#194a50]/10 bg-[#f5f0e8]/40 p-3 text-sm text-[#0c2431] space-y-1">
                      <p>
                        <strong>{t('booking_detail.edit_dates.current') || 'Current'}:</strong>{' '}
                        {booking.start_date?.slice(0, 10)} → {booking.end_date?.slice(0, 10)}{' '}
                        ({booking.nights || bookingNights} {t('booking_detail.edit_dates.nights') || 'nights'})
                      </p>
                      {(() => {
                        if (!editStartDate || !editEndDate) return null;
                        const newNights = Math.max(0, Math.round(
                          (new Date(editEndDate) - new Date(editStartDate)) / (1000 * 60 * 60 * 24)
                        ));
                        const oldNights = booking.nights || bookingNights;
                        const delta = newNights - oldNights;
                        let hint = null;
                        if (delta > 0) {
                          hint = (
                            <span className="text-[#c8724a]">
                              {t('booking_detail.edit_dates.hint_longer') ||
                                `+${delta} night(s) — extra charge will be added to the balance.`}
                            </span>
                          );
                        } else if (delta < 0) {
                          hint = (
                            <span className="text-[#194a50]">
                              {t('booking_detail.edit_dates.hint_shorter') ||
                                `${delta} night(s) — refund difference from security deposit at check-out.`}
                            </span>
                          );
                        } else {
                          hint = (
                            <span className="text-[#194a50]/70">
                              {t('booking_detail.edit_dates.hint_same') || 'Same total nights — no extra charge.'}
                            </span>
                          );
                        }
                        return (
                          <p>
                            <strong>{t('booking_detail.edit_dates.new') || 'New'}:</strong>{' '}
                            {editStartDate} → {editEndDate} ({newNights}{' '}
                            {t('booking_detail.edit_dates.nights') || 'nights'}) — {hint}
                          </p>
                        );
                      })()}
                      {!isAdminOrCS && (
                        <p className="text-xs text-[#194a50]/60 pt-1">
                          {t('booking_detail.edit_dates.cs_notice') ||
                            'Customer service will be notified of this change by email.'}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setEditDatesOpen(false)} disabled={savingDates}>
                        {t('common.cancel') || 'Cancel'}
                      </Button>
                      <Button
                        type="submit"
                        className="bg-[#194a50] hover:bg-[#10555e]"
                        disabled={savingDates}
                        data-testid="submit-edit-dates-btn"
                      >
                        {savingDates && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {t('booking_detail.edit_dates.submit') || 'Update dates'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {isAdminOrCS && (
                <div className="pt-4 border-t border-[#194a50]/10">
                  <p className="text-sm text-[#194a50]/60 mb-2">{t('booking_detail.info.update_status')}</p>
                  <div className="flex gap-2">
                    <Select value={booking.status} onValueChange={handleStatusChange}>
                      <SelectTrigger className="w-40" data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PENDING">{t('booking_detail.status.pending')}</SelectItem>
                        <SelectItem value="CONFIRMED">{t('booking_detail.status.confirmed')}</SelectItem>
                        <SelectItem value="CANCELLED">{t('booking_detail.status.cancelled')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={booking.payment_status} onValueChange={handlePaymentStatusChange}>
                      <SelectTrigger className="w-48" data-testid="payment-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DEPOSIT_DUE">{t('booking_detail.status.deposit_due')}</SelectItem>
                        <SelectItem value="DEPOSIT_PAID">{t('booking_detail.status.deposit_paid')}</SelectItem>
                        <SelectItem value="BALANCE_DUE">{t('booking_detail.status.balance_due')}</SelectItem>
                        <SelectItem value="BALANCE_PAID">{t('booking_detail.status.balance_paid')}</SelectItem>
                        <SelectItem value="REFUNDED">{t('booking_detail.status.refunded')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {canEditDates && (
                <div className="pt-4 border-t border-[#194a50]/10">
                  <p className="text-sm text-[#194a50]/60 mb-2">
                    {t('booking_detail.edit_dates.section_label') || 'Actions'}
                  </p>
                  <Button
                    variant="outline"
                    onClick={openEditDates}
                    data-testid="edit-dates-btn"
                    className="border-[#c8724a] text-[#c8724a] hover:bg-[#c8724a]/10 hover:text-[#c8724a]"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    {t('booking_detail.edit_dates.button') || 'Edit booking'}
                  </Button>
                </div>
              )}

              {isAdmin && (
                <div className="pt-4 border-t border-red-100">
                  <p className="text-sm text-red-700/80 mb-2">Danger zone</p>
                  <Button
                    variant="outline"
                    onClick={handleDeleteBooking}
                    data-testid="delete-booking-btn"
                    className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete booking
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Guest Information (hidden for OWNER) */}
          {!isOwner && primaryGuest && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <User className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.guest.primary_title')}
                </CardTitle>
                {isAdminOrCS && (
                  <Dialog open={editGuestOpen} onOpenChange={setEditGuestOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" onClick={() => openEditGuest(primaryGuest)} data-testid="edit-guest-btn">
                        <Pencil className="w-4 h-4 mr-2" />
                        {t('common.edit')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white">
                      <DialogHeader>
                        <DialogTitle>{t('booking_detail.guest.edit_title')}</DialogTitle>
                      </DialogHeader>
                      <form
                        onSubmit={(e) => { e.preventDefault(); handleSaveGuest(primaryGuest.id); }}
                        className="space-y-3 mt-4"
                      >
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>{t('booking_detail.guest.first_name')}</Label>
                            <Input value={guestForm.first_name || ''} onChange={(e) => setGuestForm({ ...guestForm, first_name: e.target.value })} data-testid="guest-first-name-input" />
                          </div>
                          <div>
                            <Label>{t('booking_detail.guest.last_name')}</Label>
                            <Input value={guestForm.last_name || ''} onChange={(e) => setGuestForm({ ...guestForm, last_name: e.target.value })} data-testid="guest-last-name-input" />
                          </div>
                          <div>
                            <Label>{t('booking_detail.guest.email')}</Label>
                            <Input type="email" value={guestForm.email || ''} onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })} />
                          </div>
                          <div>
                            <Label>{t('booking_detail.guest.phone')}</Label>
                            <Input value={guestForm.phone || ''} onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })} />
                          </div>
                          <div>
                            <Label>{t('booking_detail.guest.id_type')}</Label>
                            <Input value={guestForm.id_type || ''} onChange={(e) => setGuestForm({ ...guestForm, id_type: e.target.value })} />
                          </div>
                          <div>
                            <Label>{t('booking_detail.guest.id_number')}</Label>
                            <Input value={guestForm.id_number || ''} onChange={(e) => setGuestForm({ ...guestForm, id_number: e.target.value })} />
                          </div>
                          <div>
                            <Label>{t('booking_detail.guest.nationality')}</Label>
                            <Input value={guestForm.nationality || ''} onChange={(e) => setGuestForm({ ...guestForm, nationality: e.target.value })} />
                          </div>
                          <div>
                            <Label>{t('booking_detail.guest.date_of_birth')}</Label>
                            <Input type="date" value={guestForm.date_of_birth || ''} onChange={(e) => setGuestForm({ ...guestForm, date_of_birth: e.target.value })} />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                          <Button type="button" variant="outline" onClick={() => setEditGuestOpen(false)}>{t('common.cancel')}</Button>
                          <Button type="submit" className="bg-[#194a50] hover:bg-[#10555e]" disabled={savingGuest} data-testid="save-guest-btn">
                            {savingGuest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            {t('common.save')}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-[#194a50]/60">{t('booking_detail.guest.name')}</p>
                    <p className="font-medium text-[#0c2431]">{primaryGuest.first_name} {primaryGuest.last_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#194a50]/60">{t('booking_detail.guest.email')}</p>
                    <p className="font-medium text-[#0c2431]">{primaryGuest.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#194a50]/60">{t('booking_detail.guest.phone')}</p>
                    <p className="font-medium text-[#0c2431]">{primaryGuest.phone || '-'}</p>
                  </div>
                  {isAdmin && primaryGuest.id_type && (
                    <div>
                      <p className="text-sm text-[#194a50]/60">{t('booking_detail.guest.id_label', { type: primaryGuest.id_type })}</p>
                      <p className="font-medium text-[#0c2431]">{primaryGuest.id_number || '-'}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Additional Guests */}
          {!isOwner && additionalGuests.length > 0 && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <Users className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.guest.additional_title', { n: additionalGuests.length })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {additionalGuests.map((guest, idx) => (
                    <div key={guest.id || idx} className="p-3 bg-[#e1dad2]/30 rounded-lg">
                      <p className="font-medium text-[#0c2431]">{guest.first_name} {guest.last_name}</p>
                      <p className="text-sm text-[#194a50]/60">{guest.email}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Records (hidden for OWNER) */}
          {!isOwner && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <FileText className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.payments.title')}
                </CardTitle>
                {isAdminOrCS && (
                  <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-[#194a50] hover:bg-[#10555e]" data-testid="add-payment-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('booking_detail.payments.record_button')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white">
                      <DialogHeader>
                        <DialogTitle>{t('booking_detail.payments.dialog_title')}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddPayment} className="space-y-4 mt-4">
                        <div>
                          <Label>{t('booking_detail.payments.amount')}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={paymentForm.amount}
                            onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                            required
                            data-testid="payment-amount-input"
                          />
                        </div>
                        <div>
                          <Label>{t('booking_detail.payments.type')}</Label>
                          <Select value={paymentForm.type} onValueChange={(val) => setPaymentForm({ ...paymentForm, type: val })}>
                            <SelectTrigger data-testid="payment-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="BANK_TRANSFER">{t('booking_detail.payments.bank_transfer_fee')}</SelectItem>
                              <SelectItem value="STRIPE_EU">{t('booking_detail.payments.stripe_eu')}</SelectItem>
                              <SelectItem value="STRIPE_INTL">{t('booking_detail.payments.stripe_intl')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>{t('booking_detail.payments.payment_for')}</Label>
                          <Select
                            value={paymentForm.description || ''}
                            onValueChange={(val) => setPaymentForm({ ...paymentForm, description: val })}
                          >
                            <SelectTrigger data-testid="payment-for-select">
                              <SelectValue placeholder={t('booking_detail.payments.select_payment_type')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Booking Deposit">{t('booking_detail.payments.option_booking_deposit')}</SelectItem>
                              <SelectItem value="Security Deposit">{t('booking_detail.payments.option_security_deposit')}</SelectItem>
                              <SelectItem value="Balance Payment">{t('booking_detail.payments.option_balance_payment')}</SelectItem>
                              <SelectItem value="Security Deposit Return">{t('booking_detail.payments.option_security_return')}</SelectItem>
                              <SelectItem value="Refund">{t('booking_detail.payments.option_refund')}</SelectItem>
                              {isLongTermBooking && (
                                <SelectItem value="Monthly Rent">{t('booking_detail.payments.option_monthly_rent')}</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Which billing month this payment settles. A bank transfer
                            arrives with no month on it, so without this the month
                            stays pending and the reminders keep going out. */}
                        {isLongTermBooking && monthlySchedule.length > 0 && (
                          <div>
                            <Label>{t('booking_detail.payments.apply_to_month')}</Label>
                            <Select
                              value={paymentForm.month_number || 'none'}
                              onValueChange={(val) => {
                                if (val === 'none') {
                                  setPaymentForm({ ...paymentForm, month_number: '', meter_reading: '' });
                                  return;
                                }
                                const month = monthlySchedule.find((m) => String(m.month_number) === val);
                                const expected = month?.amount_total ?? month?.amount_rent ?? null;
                                setPaymentForm({
                                  ...paymentForm,
                                  month_number: val,
                                  description: paymentForm.description || 'Monthly Rent',
                                  // Only fill an amount nobody has typed yet.
                                  amount: paymentForm.amount ? paymentForm.amount : (expected ?? 0)
                                });
                              }}
                            >
                              <SelectTrigger data-testid="payment-month-select">
                                <SelectValue placeholder={t('booking_detail.payments.no_month')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">{t('booking_detail.payments.no_month')}</SelectItem>
                                {monthlySchedule.map((m) => (
                                  <SelectItem
                                    key={m.month_number}
                                    value={String(m.month_number)}
                                    disabled={m.status === 'paid'}
                                  >
                                    {t('booking_detail.payments.month_option', {
                                      n: m.month_number,
                                      due: m.due_date ? new Date(m.due_date).toLocaleDateString(loc) : '—'
                                    })}
                                    {m.status === 'paid' && ` · ${t('booking_detail.payments.month_paid')}`}
                                    {m.status === 'overdue' && ` · ${t('booking_detail.payments.month_overdue')}`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-[#194a50]/50 mt-1">{t('booking_detail.payments.apply_to_month_hint')}</p>
                          </div>
                        )}
                        {paymentForm.month_number && (
                          <div>
                            <Label>{t('booking_detail.payments.meter_reading_optional')}</Label>
                            <Input
                              type="number"
                              step="0.001"
                              value={paymentForm.meter_reading}
                              onChange={(e) => setPaymentForm({ ...paymentForm, meter_reading: e.target.value })}
                              placeholder={t('booking_detail.payments.meter_reading_placeholder')}
                              data-testid="payment-meter-input"
                            />
                            <p className="text-xs text-[#194a50]/50 mt-1">{t('booking_detail.payments.meter_reading_hint')}</p>
                          </div>
                        )}
                        <div>
                          <Label>{t('booking_detail.payments.notes_optional')}</Label>
                          <Textarea
                            value={paymentForm.notes || ''}
                            onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                            placeholder={t('booking_detail.payments.notes_placeholder')}
                          />
                        </div>
                        {paymentForm.type === 'BANK_TRANSFER' && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <p className="text-sm text-amber-800">
                              <strong>{t('booking_detail.payments.bank_transfer_notice_title')}</strong> {t('booking_detail.payments.bank_transfer_notice_body')}
                            </p>
                          </div>
                        )}
                        {(paymentForm.type === 'STRIPE_EU' || paymentForm.type === 'STRIPE_INTL' || paymentForm.type === 'STRIPE') && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm text-blue-800">
                              <strong>{t('booking_detail.payments.stripe_notice_title')}</strong> {t('booking_detail.payments.stripe_notice_body')}
                              {paymentForm.type === 'STRIPE_EU' && ' ' + t('booking_detail.payments.stripe_eu_rate')}
                              {paymentForm.type === 'STRIPE_INTL' && ' ' + t('booking_detail.payments.stripe_intl_rate')}
                              {' '}{t('booking_detail.payments.confirmed_immediately')}
                            </p>
                          </div>
                        )}
                        <div className="flex justify-end gap-3">
                          <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>{t('common.cancel')}</Button>
                          <Button type="submit" className="bg-[#194a50] hover:bg-[#10555e]" data-testid="submit-payment-btn">
                            {t('booking_detail.payments.record_button')}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {/* Live balance — payments are the source of truth */}
                {paymentSummary && (
                  <div className="flex justify-between items-center mb-4 p-3 rounded-lg bg-[#194a50]/5 text-sm">
                    <span className="text-[#194a50]/70">
                      {t('booking_detail.payments.total_paid')}:{' '}
                      <span className="font-semibold text-[#0c2431]">€{paymentSummary.total_paid?.toLocaleString(loc)}</span>
                    </span>
                    <span className={paymentSummary.is_fully_paid ? 'text-green-700 font-semibold' : 'text-[#0c2431] font-semibold'}>
                      {t('booking_detail.payments.balance_remaining')}: €{paymentSummary.balance_remaining?.toLocaleString(loc)}
                    </span>
                  </div>
                )}
                {payments.length === 0 ? (
                  <p className="text-[#194a50]/60 text-center py-4">{t('booking_detail.payments.no_payments')}</p>
                ) : (
                  <div className="space-y-3">
                    {payments.map((payment) => {
                      const voided = payment.status === 'VOIDED';
                      return (
                      <div key={payment.id} className={`flex items-center justify-between p-3 rounded-lg ${voided ? 'bg-gray-100 opacity-70' : 'bg-[#e1dad2]/30'}`}>
                        <div>
                          <p className={`font-medium text-[#0c2431] ${voided ? 'line-through' : ''}`}>€{payment.amount?.toLocaleString(loc)}</p>
                          <p className="text-sm text-[#194a50]/60">
                            {payment.type?.replace('_', ' ')} • {payment.description || t('booking_detail.payments.payment_label')}
                            {payment.month_number ? (
                              <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-[#194a50]/10 text-[11px] font-medium text-[#194a50]">
                                {t('booking_detail.payments.month_badge', { n: payment.month_number })}
                              </span>
                            ) : null}
                          </p>
                          <p className="text-xs text-[#194a50]/50">
                            {new Date(payment.paid_at || payment.created_at).toLocaleDateString(loc)}
                            {payment.fee_amount > 0 && ` • ${t('booking_detail.payments.fee_label', { amount: payment.fee_amount?.toFixed(2) })}`}
                          </p>
                          {payment.notes && <p className="text-xs text-[#194a50]/50 mt-1">{payment.notes}</p>}
                          {voided && payment.void_reason && (
                            <p className="text-xs text-red-500 mt-1">{t('booking_detail.payments.voided')}: {payment.void_reason}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${
                            voided ? 'bg-gray-200 text-gray-600' :
                            payment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            payment.status === 'PENDING' ? 'bg-amber-100 text-amber-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {voided ? t('booking_detail.payments.voided') : payment.status}
                          </Badge>
                          {isAdmin && !voided && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 px-2"
                              onClick={() => handleVoidPayment(payment)}
                              disabled={voidingPaymentId === payment.id}
                              title={t('booking_detail.payments.void')}
                              data-testid={`void-payment-${payment.id}`}
                            >
                              {voidingPaymentId === payment.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    );})}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Recorded Charges — Admin/CS only.
              Costs are logged on the day they happen; the invoice covering that
              date collects them. Before this they could only be typed into the
              generate dialog, so anything not remembered that day was lost. */}
          {isAdminOrCS && !booking.is_owner_reservation && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <Wrench className="w-5 h-5 text-[#c8724a]" />
                  {t('booking_detail.charges.title')}
                  {chargeTotals && (chargeTotals.pending_deductions > 0 || chargeTotals.pending_charges > 0) && (
                    <Badge className="ml-2 bg-amber-100 text-amber-800">
                      {t('booking_detail.charges.pending_badge', {
                        amount: (chargeTotals.pending_deductions + chargeTotals.pending_charges).toFixed(2),
                      })}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-[#194a50]/60">
                  {t('booking_detail.charges.help')}
                </p>

                {bookingCharges.length === 0 && (
                  <p className="text-sm text-[#194a50]/50" data-testid="charges-empty">
                    {t('booking_detail.charges.empty')}
                  </p>
                )}

                {bookingCharges.length > 0 && (
                  <div className="space-y-1" data-testid="charges-list">
                    {bookingCharges.map((charge) => {
                      const isVoid = charge.status === 'void';
                      const isBilled = charge.status === 'billed';
                      return (
                        <div
                          key={charge.id}
                          className={`flex items-start justify-between gap-3 p-2 rounded text-xs ${
                            isVoid ? 'bg-gray-50 text-[#194a50]/40'
                              : charge.type === 'deduction' ? 'bg-red-50' : 'bg-green-50'
                          }`}
                          data-testid={`charge-${charge.id}`}
                        >
                          <div className="min-w-0">
                            <div className={`font-medium ${isVoid ? 'line-through' : ''}`}>
                              {charge.description}
                            </div>
                            <div className="text-[11px] text-[#194a50]/60 mt-0.5">
                              {new Date(charge.date).toLocaleDateString(loc)}
                              {' · '}
                              {isBilled
                                ? t('booking_detail.charges.on_invoice', {
                                    number: charge.invoice_number || charge.invoice_id,
                                  })
                                : isVoid
                                  ? t('booking_detail.charges.void_with_reason', {
                                      reason: charge.void_reason || t('booking_detail.charges.no_reason'),
                                    })
                                  : t('booking_detail.charges.pending')}
                              {charge.created_by_email && ` · ${charge.created_by_email}`}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`font-medium ${
                              isVoid ? '' : charge.type === 'deduction' ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {charge.type === 'deduction' ? '-' : '+'}€{parseFloat(charge.amount).toFixed(2)}
                            </span>
                            {charge.status === 'pending' && (
                              <button
                                onClick={() => handleVoidCharge(charge)}
                                disabled={voidingChargeId === charge.id}
                                className="text-red-500 hover:text-red-700 disabled:opacity-40"
                                title={t('booking_detail.charges.void')}
                                data-testid={`void-charge-${charge.id}`}
                              >
                                {voidingChargeId === charge.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <Trash2 className="w-3 h-3" />}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Record a new one. The date is the day the cost was incurred —
                    it decides which invoice picks the charge up, so it is not
                    defaulted to today. */}
                <div className="border-t pt-3 grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3">
                    <Label className="text-xs">{t('booking_detail.charges.date')}</Label>
                    <Input
                      type="date"
                      value={newCharge.date}
                      min={booking.start_date?.slice(0, 10)}
                      onChange={(e) => setNewCharge({ ...newCharge, date: e.target.value })}
                      className="text-xs h-8 mt-1"
                      data-testid="charge-date"
                    />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">{t('booking_detail.charges.description')}</Label>
                    <Input
                      placeholder={t('booking_detail.charges.description_placeholder')}
                      value={newCharge.description}
                      onChange={(e) => setNewCharge({ ...newCharge, description: e.target.value })}
                      className="text-xs h-8 mt-1"
                      data-testid="charge-description"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">{t('booking_detail.charges.amount')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="60.00"
                      value={newCharge.amount}
                      onChange={(e) => setNewCharge({ ...newCharge, amount: e.target.value })}
                      className="text-xs h-8 mt-1"
                      data-testid="charge-amount"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">{t('booking_detail.charges.direction')}</Label>
                    <Select
                      value={newCharge.type}
                      onValueChange={(val) => setNewCharge({ ...newCharge, type: val })}
                    >
                      <SelectTrigger className="h-8 text-xs mt-1" data-testid="charge-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="deduction">{t('booking_detail.invoice.deduct')}</SelectItem>
                        <SelectItem value="charge">{t('booking_detail.invoice.charge')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-full px-2"
                      onClick={handleRecordCharge}
                      disabled={savingCharge}
                      data-testid="add-charge-btn"
                    >
                      {savingCharge
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Plus className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Owner Invoice - Admin/CS only */}
          {isAdminOrCS && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <Receipt className="w-5 h-5 text-green-600" />
                  {ownerInvoices.length > 1 || (monthlyInvoiceStatus?.is_long_term && monthlyInvoiceStatus?.invoices_pending > 0)
                    ? t('booking_detail.invoice.title_many')
                    : t('booking_detail.invoice.title_one')}
                  {monthlyInvoiceStatus?.is_long_term && (
                    <Badge className="ml-2 bg-blue-100 text-blue-800">
                      {t('booking_detail.invoice.monthly_badge', { done: monthlyInvoiceStatus.invoices_generated, total: monthlyInvoiceStatus.total_months })}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Long-term booking: Show monthly invoice status */}
                {monthlyInvoiceStatus?.is_long_term && (
                  <div className="mb-4 space-y-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <h4 className="font-medium text-blue-900 text-sm">{t('booking_detail.invoice.monthly_status_title')}</h4>
                      <p className="text-xs text-blue-700 mt-1">
                        {t('booking_detail.invoice.monthly_status_body', { done: monthlyInvoiceStatus.invoices_generated, total: monthlyInvoiceStatus.total_months })}
                      </p>
                    </div>

                    {/* Pending months - show as cards to generate */}
                    {monthlyInvoiceStatus.monthly_status?.filter(m => !m.has_invoice).length > 0 && (
                      <div className="space-y-2">
                        <h5 className="text-sm font-medium text-[#0c2431]">{t('booking_detail.invoice.pending_invoices')}</h5>
                        {monthlyInvoiceStatus.monthly_status.filter(m => !m.has_invoice).map((month) => (
                          <div key={month.month} className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <span className="font-medium text-amber-900">{t('booking_detail.invoice.month_label', { n: month.month })}</span>
                                <span className="text-xs text-amber-700 ml-2">
                                  {new Date(month.start_date).toLocaleDateString(loc)} - {new Date(month.end_date).toLocaleDateString(loc)}
                                </span>
                                <Badge className="ml-2 text-xs bg-amber-100 text-amber-800">{t('booking_detail.invoice.nights_n', { n: month.nights })}</Badge>
                              </div>
                            </div>

                            {selectedMonth === month.month ? (
                              <div className="mt-3 space-y-3 bg-white rounded p-3">
                                <p className="text-xs text-[#194a50]/70">{t('booking_detail.invoice.enter_readings_prompt')}</p>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs">{t('booking_detail.invoice.start_reading')}</Label>
                                    <Input
                                      type="number"
                                      placeholder="e.g., 1000"
                                      value={monthElectricity.start_kwh}
                                      onChange={(e) => { setMonthlyPreview(null); setMonthElectricity({...monthElectricity, start_kwh: e.target.value}); }}
                                      className="mt-1"
                                      data-testid={`elec-start-month-${month.month}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">{t('booking_detail.invoice.end_reading')}</Label>
                                    <Input
                                      type="number"
                                      placeholder="e.g., 1250"
                                      value={monthElectricity.end_kwh}
                                      onChange={(e) => { setMonthlyPreview(null); setMonthElectricity({...monthElectricity, end_kwh: e.target.value}); }}
                                      className="mt-1"
                                      data-testid={`elec-end-month-${month.month}`}
                                    />
                                  </div>
                                </div>
                                {/* Where the figures above came from. A pre-filled
                                    field the admin cannot account for is worse
                                    than a blank one. */}
                                {(() => {
                                  const elec = month.electricity || {};
                                  if (elec.end_source === 'reading') {
                                    return (
                                      <p className="text-xs text-green-700">
                                        {t('booking_detail.invoice.readings_from_tenant', { n: month.month })}
                                      </p>
                                    );
                                  }
                                  if (elec.end_source === 'consumption') {
                                    return (
                                      <p className="text-xs text-green-700">
                                        {t('booking_detail.invoice.readings_from_consumption', { n: month.month, kwh: elec.consumption_kwh })}
                                      </p>
                                    );
                                  }
                                  if (elec.start_source === 'month') {
                                    return (
                                      <p className="text-xs text-[#194a50]/60">
                                        {t('booking_detail.invoice.start_from_month', { n: elec.start_month })}
                                      </p>
                                    );
                                  }
                                  // On a booking that settles by bank transfer the
                                  // tenant submits no readings, so the last one
                                  // taken is the one typed into an owner invoice.
                                  if (elec.start_source === 'invoice') {
                                    return (
                                      <p className="text-xs text-[#194a50]/60">
                                        {t('booking_detail.invoice.start_from_invoice', { n: elec.start_month })}
                                      </p>
                                    );
                                  }
                                  if (elec.start_source === 'check_in' || elec.start_source === 'reconstructed') {
                                    return (
                                      <p className="text-xs text-[#194a50]/60">
                                        {t('booking_detail.invoice.start_from_check_in')}
                                      </p>
                                    );
                                  }
                                  return (
                                    <p className="text-xs text-amber-700">
                                      {t('booking_detail.invoice.start_unknown')}
                                    </p>
                                  );
                                })()}
                                {parseFloat(monthElectricity.end_kwh) > parseFloat(monthElectricity.start_kwh) && (
                                  <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                                    {t('booking_detail.invoice.consumption', { n: (parseFloat(monthElectricity.end_kwh) - parseFloat(monthElectricity.start_kwh)).toFixed(1) })}
                                  </div>
                                )}

                                {/* Monthly Adjustments */}
                                <div className="border-t pt-3 mt-3">
                                  <Label className="text-xs font-semibold">{t('booking_detail.invoice.adjustments_label')}</Label>
                                  <p className="text-xs text-[#194a50]/60 mb-2">
                                    {t('booking_detail.invoice.adjustments_help')}
                                  </p>
                                  
                                  {/* Existing adjustments list */}
                                  {monthlyAdjustments.length > 0 && (
                                    <div className="space-y-1 mb-2">
                                      {monthlyAdjustments.map((adj, idx) => (
                                        <div key={idx} className={`flex items-center justify-between p-2 rounded text-xs ${
                                          adj.type === 'deduction' ? 'bg-red-50' : 'bg-green-50'
                                        }`}>
                                          <div className="flex items-center gap-2">
                                            <span className={`font-medium px-1.5 py-0.5 rounded ${
                                              adj.type === 'deduction' ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'
                                            }`}>
                                              {adj.type === 'deduction' ? '-' : '+'}
                                            </span>
                                            <span>{adj.description}</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className={`font-medium ${adj.type === 'deduction' ? 'text-red-600' : 'text-green-600'}`}>
                                              €{parseFloat(adj.amount).toFixed(2)}
                                            </span>
                                            <button 
                                              onClick={() => { setMonthlyPreview(null); setMonthlyAdjustments(monthlyAdjustments.filter((_, i) => i !== idx)); }}
                                              className="text-red-500 hover:text-red-700"
                                            >
                                              <Trash2 className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* Add new adjustment form */}
                                  <div className="flex gap-2 items-end">
                                    <div className="flex-1">
                                      <Input
                                        placeholder={t('booking_detail.invoice.description')}
                                        value={newMonthlyAdjustment.description}
                                        onChange={(e) => setNewMonthlyAdjustment({...newMonthlyAdjustment, description: e.target.value})}
                                        className="text-xs h-8"
                                        data-testid={`adj-desc-month-${month.month}`}
                                      />
                                    </div>
                                    <div className="w-20">
                                      <Input
                                        type="number"
                                        placeholder={t('booking_detail.invoice.amount')}
                                        value={newMonthlyAdjustment.amount}
                                        onChange={(e) => setNewMonthlyAdjustment({...newMonthlyAdjustment, amount: e.target.value})}
                                        className="text-xs h-8"
                                        data-testid={`adj-amount-month-${month.month}`}
                                      />
                                    </div>
                                    <Select 
                                      value={newMonthlyAdjustment.type} 
                                      onValueChange={(val) => setNewMonthlyAdjustment({...newMonthlyAdjustment, type: val})}
                                    >
                                      <SelectTrigger className="w-24 h-8 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="deduction">{t('booking_detail.invoice.deduct')}</SelectItem>
                                        <SelectItem value="charge">{t('booking_detail.invoice.charge')}</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      className="h-8 px-2"
                                      onClick={() => {
                                        if (newMonthlyAdjustment.description && newMonthlyAdjustment.amount) {
                                          setMonthlyPreview(null);
                                          setMonthlyAdjustments([...monthlyAdjustments, {...newMonthlyAdjustment, id: Date.now()}]);
                                          setNewMonthlyAdjustment({ description: '', amount: '', type: 'deduction' });
                                        }
                                      }}
                                      data-testid={`add-adj-month-${month.month}`}
                                    >
                                      <Plus className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* Month preview — the same figures the invoice
                                    will carry, shown before it gets a number */}
                                <div className="border-t pt-3 mt-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <Label className="text-xs font-semibold">
                                      {t('booking_detail.invoice.month_preview_title', { n: month.month })}
                                    </Label>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleCalculateMonthlyPayout(month.month)}
                                      disabled={loadingMonthlyPreview}
                                      data-testid={`calculate-month-${month.month}-btn`}
                                    >
                                      {loadingMonthlyPreview ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        t('booking_detail.invoice.calculate')
                                      )}
                                    </Button>
                                  </div>

                                  {!monthlyPreview && (
                                    <p className="text-xs text-[#194a50]/60">
                                      {t('booking_detail.invoice.month_preview_hint')}
                                    </p>
                                  )}

                                  {monthlyPreview && (() => {
                                    const sum = monthlyPreview.summary || {};
                                    const fee = monthlyPreview.agent_fee || {};
                                    const per = monthlyPreview.period || {};
                                    const elec = monthlyPreview.electricity || {};
                                    const adj = monthlyPreview.adjustments || {};
                                    const invoiceTotal = sum.invoice_amount ?? fee.gross ?? 0;
                                    return (
                                      <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-2" data-testid={`month-preview-${month.month}`}>
                                        {monthlyPreview.already_invoiced && (
                                          <div className="bg-red-50 border border-red-200 text-red-800 rounded p-2">
                                            {t('booking_detail.invoice.month_already_invoiced', { number: monthlyPreview.already_invoiced.invoice_number })}
                                          </div>
                                        )}
                                        <div className="text-[#194a50]/70">
                                          {new Date(per.start_date).toLocaleDateString(loc)} – {new Date(per.end_date).toLocaleDateString(loc)}
                                          {' · '}
                                          {t('booking_detail.invoice.nights_n', { n: per.nights })}
                                        </div>

                                        <div className="space-y-1">
                                          <div className="text-[10px] font-semibold text-[#194a50]/70 uppercase tracking-wide">
                                            {t('booking_detail.invoice.invoice_to_owner')}
                                          </div>
                                          <div className="flex justify-between">
                                            <span>{t('booking_detail.invoice.management_fee_net', { pct: fee.rate_pct })}</span>
                                            <span>€{(fee.base ?? 0).toFixed(2)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span>{t('booking_detail.invoice.vat', { rate: fee.vat_rate })}</span>
                                            <span>€{(fee.vat ?? 0).toFixed(2)}</span>
                                          </div>
                                          <div className="flex justify-between font-bold border-t pt-1">
                                            <span>{t('booking_detail.invoice.invoice_total')}</span>
                                            <span>€{invoiceTotal.toFixed(2)}</span>
                                          </div>
                                        </div>

                                        <div className="space-y-1 border-t pt-2">
                                          <div className="text-[10px] font-semibold text-[#194a50]/70 uppercase tracking-wide">
                                            {t('booking_detail.invoice.owner_payout_section')}
                                          </div>
                                          <div className="flex justify-between">
                                            <span>{t('booking_detail.invoice.accommodation_n_nights', { n: per.nights })}</span>
                                            <span>€{(monthlyPreview.accommodation?.total ?? 0).toFixed(2)}</span>
                                          </div>
                                          <div className="flex justify-between text-red-600">
                                            <span>{t('booking_detail.invoice.agent_fee_incl_vat')}</span>
                                            <span>-€{invoiceTotal.toFixed(2)}</span>
                                          </div>
                                          <div className="flex justify-between text-amber-700">
                                            <span>{t('booking_detail.invoice.electricity_kwh_cost', { kwh: (elec.consumption_kwh ?? 0).toFixed(2) })}</span>
                                            <span>+€{(elec.total_cost ?? 0).toFixed(2)}</span>
                                          </div>
                                          {adj.total_charges > 0 && (
                                            <div className="flex justify-between text-green-600">
                                              <span>{t('booking_detail.invoice.charges')}</span>
                                              <span>+€{adj.total_charges.toFixed(2)}</span>
                                            </div>
                                          )}
                                          {adj.total_deductions > 0 && (
                                            <div className="flex justify-between text-red-600">
                                              <span>{t('booking_detail.invoice.deductions')}</span>
                                              <span>-€{adj.total_deductions.toFixed(2)}</span>
                                            </div>
                                          )}
                                          {adj.items?.length > 0 && (
                                            <div className="pl-2 space-y-0.5 text-[#194a50]/60">
                                              {adj.items.map((a, i) => (
                                                <div key={i} className={a.type === 'deduction' ? 'text-red-500' : 'text-green-500'}>
                                                  • {a.description}: €{parseFloat(a.amount).toFixed(2)}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          <div className="flex justify-between font-bold text-sm border-t pt-1 text-green-700">
                                            <span>{t('booking_detail.invoice.owner_payout_section')}</span>
                                            <span>€{(sum.owner_payout ?? 0).toFixed(2)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>

                                <div className="flex gap-2 mt-3">
                                  <Button
                                    size="sm"
                                    onClick={() => handleGenerateSingleMonthInvoice(month.month)}
                                    disabled={generatingInvoice || !monthlyPreview}
                                    title={!monthlyPreview ? t('booking_detail.invoice.preview_before_generate') : undefined}
                                    className="bg-green-600 hover:bg-green-700"
                                    data-testid={`generate-month-${month.month}-btn`}
                                  >
                                    {generatingInvoice ? (
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    ) : (
                                      <Receipt className="w-4 h-4 mr-1" />
                                    )}
                                    {t('booking_detail.invoice.generate_invoice')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedMonth(null);
                                      setMonthElectricity({ start_kwh: '', end_kwh: '' });
                                      setMonthlyAdjustments([]);
                                      setNewMonthlyAdjustment({ description: '', amount: '', type: 'deduction' });
                                      setMonthlyPreview(null);
                                    }}
                                  >
                                    {t('booking_detail.invoice.cancel')}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openMonthForInvoice(month)}
                                className="mt-2"
                                data-testid={`select-month-${month.month}-btn`}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                {t('booking_detail.invoice.generate_for_month', { n: month.month })}
                              </Button>
                              {isAdminOrCS && month.month !== 1 && (
                                <div className="mt-2 ml-2 inline-flex flex-wrap items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRegeneratePaymentLink(month.month, false)}
                                    disabled={regeneratingToken === month.month}
                                    className="text-xs"
                                    data-testid={`regen-link-month-${month.month}`}
                                  >
                                    {regeneratingToken === month.month ? (
                                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    ) : (
                                      <Link2 className="w-3 h-3 mr-1" />
                                    )}
                                    {t('booking_detail.invoice.regenerate_link')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRegeneratePaymentLink(month.month, true)}
                                    disabled={regeneratingToken === month.month}
                                    className="text-xs"
                                    data-testid={`email-link-month-${month.month}`}
                                  >
                                    <Mail className="w-3 h-3 mr-1" />
                                    {t('booking_detail.invoice.email_link')}
                                  </Button>
                                  {regeneratedLinks[month.month] && (
                                    <span className="flex items-center gap-1 text-xs text-[#194a50]/80 break-all">
                                      <a href={regeneratedLinks[month.month]} target="_blank" rel="noreferrer" className="underline">
                                        {regeneratedLinks[month.month]}
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => copyPaymentLink(regeneratedLinks[month.month])}
                                        className="p-1 rounded hover:bg-amber-100"
                                        title={t('booking_detail.invoice.copy')}
                                      >
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    </span>
                                  )}
                                </div>
                              )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {ownerInvoices.length > 0 ? (
                  <div className="space-y-4">
                    {/* Generated invoices header */}
                    {monthlyInvoiceStatus?.is_long_term && ownerInvoices.length > 0 && (
                      <h5 className="text-sm font-medium text-[#0c2431] border-t pt-3">{t('booking_detail.invoice.generated_invoices')}</h5>
                    )}
                    
                    {/* Show all invoices if multiple */}
                    {ownerInvoices.map((invoice, index) => (
                      <div key={invoice.id} className="space-y-3 border-b border-[#194a50]/10 pb-4 last:border-b-0">
                        <div className={`flex items-center justify-between p-3 rounded-lg ${
                          invoice.status === 'PAID_OUT' ? 'bg-green-100 text-green-800' :
                          invoice.status === 'PAID' ? 'bg-blue-100 text-blue-800' :
                          'bg-amber-50 text-amber-800'
                        }`}>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5" />
                            <div>
                              <span className="font-medium block">{invoice.invoice_number}</span>
                              <span className="text-xs">
                                {invoice.booking_dates?.is_monthly_invoice
                                  ? `${t('booking_detail.invoice.month_label', { n: invoice.booking_dates.month_number })} • `
                                  : ''
                                }
                                {invoice.status}
                              </span>
                            </div>
                          </div>
                          {/* Status change buttons - Admin only */}
                          <div className="flex items-center gap-2">
                            {isAdmin && invoice.status === 'GENERATED' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateInvoiceStatus('PAID', invoice.id)}
                                className="bg-white"
                                data-testid={`mark-paid-btn-${invoice.id}`}
                              >
                                {t('booking_detail.invoice.mark_as_paid')}
                              </Button>
                            )}
                            {isAdmin && invoice.status === 'PAID' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateInvoiceStatus('PAID_OUT', invoice.id)}
                                className="bg-white"
                                data-testid={`mark-paid-out-btn-${invoice.id}`}
                              >
                                {t('booking_detail.invoice.mark_as_paid_out')}
                              </Button>
                            )}
                            {isAdmin && invoice.status !== 'CANCELLED' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCancelInvoice(invoice)}
                                disabled={cancellingInvoiceId === invoice.id}
                                className="bg-white text-red-700 hover:bg-red-50 hover:text-red-800"
                                data-testid={`cancel-invoice-btn-${invoice.id}`}
                              >
                                {cancellingInvoiceId === invoice.id
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : t('booking_detail.invoice.cancel_invoice')}
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="text-sm space-y-1">
                          {ownerInvoices.length === 1 && (
                            <div className="flex justify-between">
                              <span className="text-[#194a50]/60">{t('booking_detail.invoice.owner')}</span>
                              <span className="font-medium text-[#0c2431]">{invoice.owner_name}</span>
                            </div>
                          )}
                          {invoice.booking_dates?.is_monthly_invoice && (
                            <div className="flex justify-between text-xs">
                              <span className="text-[#194a50]/60">{t('booking_detail.invoice.period')}</span>
                              <span className="text-[#0c2431]">
                                {t('booking_detail.invoice.period_range', { start: new Date(invoice.booking_dates.check_in).toLocaleDateString(loc), end: new Date(invoice.booking_dates.check_out).toLocaleDateString(loc), n: invoice.booking_dates.nights })}
                              </span>
                            </div>
                          )}
                          {/* Payment Allocation Status */}
                          {invoice.amount_due !== undefined && (
                            <div className="bg-gray-50 rounded p-2 my-2">
                              <div className="flex justify-between text-xs">
                                <span className="text-[#194a50]/60">{t('booking_detail.invoice.amount_due')}</span>
                                <span className="text-[#0c2431]">€{invoice.amount_due?.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-[#194a50]/60">{t('booking_detail.invoice.amount_paid')}</span>
                                <span className={invoice.is_fully_paid ? 'text-green-600 font-medium' : 'text-amber-600'}>
                                  €{(invoice.amount_paid || 0).toFixed(2)}
                                </span>
                              </div>
                              {invoice.is_fully_paid ? (
                                <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  {t('booking_detail.invoice.fully_paid')}
                                </div>
                              ) : (
                                <div className="text-xs text-amber-600 mt-1">
                                  {t('booking_detail.invoice.remaining', { amount: ((invoice.amount_due || 0) - (invoice.amount_paid || 0)).toFixed(2) })}
                                </div>
                              )}
                            </div>
                          )}
                          {(() => {
                            const bd = invoice.breakdown || {};
                            const acc = bd.accommodation || {};
                            const fee = bd.agent_fee || {};
                            const sm = bd.summary || {};
                            const feePct = fee.rate_pct ?? acc.agent_fee_percentage;
                            const feeBase = fee.base ?? sm.fee_base ?? bd.tax?.taxable_amount ?? 0;
                            const feeVat = fee.vat ?? sm.fee_vat ?? bd.tax?.tax_amount ?? 0;
                            const vatRate = fee.vat_rate ?? bd.tax?.rate ?? 21;
                            const invoiceTotal = invoice.invoice_amount ?? sm.invoice_amount ?? fee.gross ?? acc.agent_fee ?? 0;
                            const elecCost = bd.electricity?.total_cost ?? 0;
                            const elecKwh = bd.electricity?.consumption_kwh ?? 0;
                            const ownerPayout = invoice.owner_payout ?? sm.owner_payout ?? invoice.total_payout ?? 0;
                            return (
                            <>
                              {/* Invoice to owner (agent fee, VAT-inclusive) */}
                              <div className="text-xs font-semibold text-[#194a50]/70 uppercase tracking-wide pt-1">{t('booking_detail.invoice.invoice_to_owner')}</div>
                              <div className="flex justify-between">
                                <span className="text-[#194a50]/60">{t('booking_detail.invoice.management_fee_net', { pct: feePct })}</span>
                                <span className="text-[#0c2431]">€{feeBase.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#194a50]/60">{t('booking_detail.invoice.vat', { rate: vatRate })}</span>
                                <span className="text-[#0c2431]">€{feeVat.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between font-bold border-t pt-1">
                                <span className="text-[#0c2431]">{t('booking_detail.invoice.invoice_total')}</span>
                                <span className="text-[#0c2431]">€{invoiceTotal.toFixed(2)}</span>
                              </div>

                              {/* Owner payout (for reference) */}
                              <div className="text-xs font-semibold text-[#194a50]/70 uppercase tracking-wide border-t pt-2">{t('booking_detail.invoice.owner_payout_section')}</div>
                              <div className="flex justify-between">
                                <span className="text-[#194a50]/60">{t('booking_detail.invoice.accommodation')}</span>
                                <span className="text-[#0c2431]">€{(acc.total ?? 0).toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-red-600">
                                <span>{t('booking_detail.invoice.agent_fee_incl_vat')}</span>
                                <span>-€{invoiceTotal.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#194a50]/60">{t('booking_detail.invoice.electricity_kwh', { kwh: elecKwh.toFixed(0) })}</span>
                                <span className="text-[#0c2431]">+€{elecCost.toFixed(2)}</span>
                              </div>
                              {bd.adjustments?.total_charges > 0 && (
                                <div className="flex justify-between text-green-600">
                                  <span>{t('booking_detail.invoice.charges')}</span>
                                  <span>+€{bd.adjustments?.total_charges?.toFixed(2)}</span>
                                </div>
                              )}
                              {bd.adjustments?.total_deductions > 0 && (
                                <div className="flex justify-between text-red-600">
                                  <span>{t('booking_detail.invoice.deductions')}</span>
                                  <span>-€{bd.adjustments?.total_deductions?.toFixed(2)}</span>
                                </div>
                              )}
                              {bd.adjustments?.items?.length > 0 && (
                                <div className="text-xs text-[#194a50]/60 pl-2 space-y-0.5">
                                  {bd.adjustments.items.map((adj, i) => (
                                    <div key={i} className={adj.type === 'deduction' ? 'text-red-500' : 'text-green-500'}>
                                      • {adj.description}: €{parseFloat(adj.amount).toFixed(2)}
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="flex justify-between pt-2 border-t font-semibold">
                                <span className="text-[#0c2431]">{t('booking_detail.invoice.owner_payout_section')}</span>
                                <span className="text-green-600">€{ownerPayout.toFixed(2)}</span>
                              </div>
                            </>
                            );
                          })()}
                        </div>
                        {/* Edit (unpaid, non-monthly only) + Download */}
                        <div className="flex gap-2">
                          {isAdminOrCS && invoice.status === 'GENERATED' && !invoice.booking_dates?.month_number && invoice.invoice_type !== 'monthly_draft' && (
                            <Button
                              variant="outline"
                              className="flex-1"
                              onClick={() => openEditInvoice(invoice)}
                              data-testid={`edit-invoice-btn-${invoice.id}`}
                              size="sm"
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              {t('booking_detail.invoice.edit')}
                            </Button>
                          )}
                          <Button
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={() => handleDownloadInvoice(invoice)}
                            data-testid={`download-invoice-btn-${invoice.id}`}
                            size="sm"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {t('booking_detail.invoice.download_pdf')}
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Summary for multiple invoices */}
                    {ownerInvoices.length > 1 && (
                      <div className="space-y-3 mt-4">
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-green-800">{t('booking_detail.invoice.total_all')}</span>
                            <span className="font-bold text-lg text-green-700">
                              €{ownerInvoices.reduce((sum, inv) => sum + (inv.total_payout || 0), 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-2 text-sm">
                            <span className="text-green-700">{t('booking_detail.invoice.total_paid')}</span>
                            <span className="font-medium text-green-800">
                              €{ownerInvoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {/* Auto-allocate payments button */}
                        {isAdminOrCS && payments.length > 0 && ownerInvoices.some(inv => !inv.is_fully_paid) && (
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={handleAutoAllocatePayments}
                            data-testid="auto-allocate-btn"
                          >
                            <DollarSign className="w-4 h-4 mr-2" />
                            {t('booking_detail.invoice.auto_allocate')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-center py-2">
                      <Receipt className="w-10 h-10 mx-auto mb-2 text-[#194a50]/30" />
                      <p className="text-sm text-[#194a50]/60">{t('booking_detail.invoice.no_invoice')}</p>
                    </div>
                    <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
                      <DialogTrigger asChild>
                        <Button
                          className="w-full bg-green-600 hover:bg-green-700"
                          data-testid="generate-invoice-btn"
                        >
                          <Receipt className="w-4 h-4 mr-2" />
                          {isLongTermBooking ? t('booking_detail.invoice.generate_many') : t('booking_detail.invoice.generate_single')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>
                            {isLongTermBooking ? t('booking_detail.invoice.generate_many_title') : t('booking_detail.invoice.generate_single_title')}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-6 mt-4">
                          {/* Long-term booking notice */}
                          {isLongTermBooking && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                                <div>
                                  <h4 className="font-semibold text-blue-900">{t('booking_detail.invoice.long_term_title', { n: bookingNights })}</h4>
                                  <p className="text-sm text-blue-700 mt-1">
                                    {t('booking_detail.invoice.long_term_body', { count: monthlyPeriods.length })}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* For long-term bookings, electricity is entered per-month when generating individual monthly invoices */}
                          {isLongTermBooking && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                              <strong>{t('booking_detail.invoice.long_term_note_title')}</strong> {t('booking_detail.invoice.long_term_note_body', { n: bookingNights })}
                            </div>
                          )}

                          {/* Payout Preview */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <Label className="font-semibold">{t('booking_detail.invoice.payout_preview')}</Label>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCalculatePayout}
                                disabled={loadingPayout}
                              >
                                {loadingPayout ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  t('booking_detail.invoice.calculate')
                                )}
                              </Button>
                            </div>
                            
                            {payoutPreview && (() => {
                              const s = payoutPreview.summary || {};
                              const fee = payoutPreview.agent_fee || {};
                              const feePct = fee.rate_pct ?? payoutPreview.accommodation?.agent_fee_percentage;
                              const feeBase = fee.base ?? s.fee_base ?? 0;
                              const feeVat = fee.vat ?? s.fee_vat ?? 0;
                              const vatRate = fee.vat_rate ?? payoutPreview.tax?.rate ?? 21;
                              const invoiceTotal = s.invoice_amount ?? fee.gross ?? payoutPreview.accommodation?.agent_fee ?? 0;
                              const elecCost = payoutPreview.electricity?.total_cost ?? s.electricity_cost ?? 0;
                              const elecKwh = payoutPreview.electricity?.consumption_kwh ?? 0;
                              const ownerPayout = s.owner_payout ?? s.final_payout ?? 0;
                              return (
                              <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-3">
                                {/* Invoice to owner (agent fee, VAT-inclusive) */}
                                <div className="space-y-1">
                                  <div className="text-xs font-semibold text-[#194a50]/70 uppercase tracking-wide">{t('booking_detail.invoice.invoice_to_owner')}</div>
                                  <div className="flex justify-between">
                                    <span>{t('booking_detail.invoice.management_fee_net', { pct: feePct })}</span>
                                    <span>€{feeBase.toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>{t('booking_detail.invoice.vat', { rate: vatRate })}</span>
                                    <span>€{feeVat.toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between font-bold border-t pt-1">
                                    <span>{t('booking_detail.invoice.invoice_total')}</span>
                                    <span>€{invoiceTotal.toFixed(2)}</span>
                                  </div>
                                </div>

                                {/* Owner payout (for reference) */}
                                <div className="space-y-1 border-t pt-2">
                                  <div className="text-xs font-semibold text-[#194a50]/70 uppercase tracking-wide">{t('booking_detail.invoice.owner_payout_section')}</div>
                                  <div className="flex justify-between">
                                    <span>{t('booking_detail.invoice.accommodation_n_nights', { n: payoutPreview.nights })}</span>
                                    <span>€{payoutPreview.accommodation?.total?.toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between text-red-600">
                                    <span>{t('booking_detail.invoice.agent_fee_incl_vat')}</span>
                                    <span>-€{invoiceTotal.toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between text-amber-700">
                                    <span>{t('booking_detail.invoice.electricity_kwh_cost', { kwh: elecKwh.toFixed(2) })}</span>
                                    <span>+€{elecCost.toFixed(2)}</span>
                                  </div>
                                  {payoutPreview.adjustments?.total_charges > 0 && (
                                    <div className="flex justify-between text-green-600">
                                      <span>{t('booking_detail.invoice.charges')}</span>
                                      <span>+€{payoutPreview.adjustments?.total_charges?.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {payoutPreview.adjustments?.total_deductions > 0 && (
                                    <div className="flex justify-between text-red-600">
                                      <span>{t('booking_detail.invoice.deductions')}</span>
                                      <span>-€{payoutPreview.adjustments?.total_deductions?.toFixed(2)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between font-bold text-lg border-t pt-1 text-green-700">
                                    <span>{t('booking_detail.invoice.owner_payout_section')}</span>
                                    <span>€{ownerPayout.toFixed(2)}</span>
                                  </div>
                                </div>
                              </div>
                              );
                            })()}
                          </div>

                          {/* Adjustments */}
                          <div>
                            <Label className="font-semibold mb-3 block">{t('booking_detail.invoice.adjustments_title')}</Label>
                            <p className="text-xs text-[#194a50]/60 mb-3">
                              {t('booking_detail.invoice.adjustments_long_help')}
                            </p>
                            
                            {/* Existing adjustments */}
                            {adjustments.length > 0 && (
                              <div className="space-y-2 mb-4">
                                {adjustments.map((adj) => (
                                  <div key={adj.id} className={`flex items-center justify-between p-2 rounded-lg ${
                                    adj.type === 'deduction' ? 'bg-red-50' : 'bg-green-50'
                                  }`}>
                                    <div className="flex items-center gap-2">
                                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                        adj.type === 'deduction' ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'
                                      }`}>
                                        {adj.type === 'deduction' ? t('booking_detail.invoice.deduct_badge') : t('booking_detail.invoice.charge_badge')}
                                      </span>
                                      <span className="text-sm">{adj.description}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`font-medium ${adj.type === 'deduction' ? 'text-red-600' : 'text-green-600'}`}>
                                        {adj.type === 'deduction' ? '-' : '+'}€{parseFloat(adj.amount).toFixed(2)}
                                      </span>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => handleRemoveAdjustment(adj.id)}
                                      >
                                        <Trash2 className="w-4 h-4 text-red-500" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Add new adjustment */}
                            <div className="flex gap-2">
                              <Input
                                placeholder={t('booking_detail.invoice.description_placeholder')}
                                value={newAdjustment.description}
                                onChange={(e) => setNewAdjustment({ ...newAdjustment, description: e.target.value })}
                                className="flex-1"
                              />
                              <Input
                                type="number"
                                placeholder={t('booking_detail.invoice.amount')}
                                value={newAdjustment.amount}
                                onChange={(e) => setNewAdjustment({ ...newAdjustment, amount: e.target.value })}
                                className="w-24"
                              />
                              <Select value={newAdjustment.type} onValueChange={(val) => setNewAdjustment({ ...newAdjustment, type: val })}>
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="deduction">{t('booking_detail.invoice.deduction')}</SelectItem>
                                  <SelectItem value="charge">{t('booking_detail.invoice.charge')}</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button variant="outline" onClick={handleAddAdjustment}>
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Notes */}
                          <div>
                            <Label className="mb-2 block">{t('booking_detail.invoice.invoice_notes_label')}</Label>
                            <Textarea
                              placeholder={t('booking_detail.invoice.invoice_notes_placeholder')}
                              value={invoiceNotes}
                              onChange={(e) => setInvoiceNotes(e.target.value)}
                              rows={3}
                            />
                          </div>

                          {/* Actions */}
                          <div className="flex justify-end gap-3 pt-4 border-t">
                            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>
                              {t('booking_detail.invoice.cancel')}
                            </Button>
                            <Button
                              className="bg-green-600 hover:bg-green-700"
                              onClick={handleGenerateInvoice}
                              disabled={generatingInvoice}
                              data-testid="confirm-generate-invoice-btn"
                            >
                              {generatingInvoice ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  {t('booking_detail.invoice.generating')}
                                </>
                              ) : (
                                <>
                                  <Receipt className="w-4 h-4 mr-2" />
                                  {t('booking_detail.invoice.generate_invoice')}
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Activity Log — CENTER COLUMN ─────────────── */}
          {isAdminOrCS && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <Activity className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.activity.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add note input */}
                <div className="flex gap-2">
                  <input
                    className="flex-1 text-sm border border-[#194a50]/20 rounded-lg px-3 py-2 focus:outline-none focus:border-[#194a50] placeholder:text-[#194a50]/40"
                    placeholder={t('booking_detail.activity.add_note_placeholder')}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddNote()}
                  />
                  <Button
                    size="sm"
                    className="bg-[#194a50] hover:bg-[#10555e] shrink-0"
                    onClick={handleAddNote}
                    disabled={addingNote || !noteText.trim()}
                  >
                    {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>

                {/* Timeline */}
                {activityLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#194a50]/40" />
                  </div>
                ) : activityLog.length === 0 ? (
                  <p className="text-sm text-center text-[#194a50]/40 py-6">{t('booking_detail.activity.no_activity')}</p>
                ) : (
                  <div className="relative pl-1">
                    {/* Vertical timeline line */}
                    <div className="absolute left-4 top-2 bottom-2 w-px bg-[#194a50]/10" />
                    <div className="space-y-4">
                      {[...activityLog].reverse().map((event, idx) => {
                        const cfg = getActivityCfg(event.action);
                        const Icon = cfg.icon;
                        return (
                          <div key={event.id || idx} className="flex gap-3">
                            <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${cfg.bg}`}>
                              <Icon className={`w-4 h-4 ${cfg.color}`} />
                            </div>
                            <div className="flex-1 min-w-0 pt-0.5">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-[#0c2431] leading-tight">
                                    {event.description || cfg.label}
                                  </p>
                                  {event.diff && (
                                    <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                      {Object.entries(event.diff).map(([field, change]) => (
                                        <span key={field} className="text-xs text-[#194a50]/60">
                                          {field}: <span className="line-through text-red-400">{change.old || '—'}</span>
                                          {' → '}
                                          <span className="text-green-600 font-medium">{change.new}</span>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {event.metadata?.amount && (
                                    <p className="text-xs text-[#194a50]/60 mt-0.5">
                                      {t('booking_detail.activity.amount_label', { amount: parseFloat(event.metadata.amount).toLocaleString(loc) })}
                                      {event.metadata.payment_type && ` · ${event.metadata.payment_type.replace('_', ' ')}`}
                                    </p>
                                  )}
                                  {event.metadata?.check_in_kwh != null && (
                                    <p className="text-xs text-[#194a50]/60 mt-0.5">
                                      {t('booking_detail.activity.checkin_kwh', { n: event.metadata.check_in_kwh })}
                                      {event.metadata.check_out_kwh != null && ` · ${t('booking_detail.activity.checkout_kwh', { n: event.metadata.check_out_kwh })}`}
                                    </p>
                                  )}
                                  {event.metadata?.invoice_number && (
                                    <p className="text-xs text-[#194a50]/60 mt-0.5">
                                      {t('booking_detail.activity.invoice_label', { number: event.metadata.invoice_number })}
                                      {event.metadata.total_payout != null && ` · ${t('booking_detail.activity.payout_label', { amount: parseFloat(event.metadata.total_payout).toFixed(2) })}`}
                                    </p>
                                  )}
                                </div>
                                <span className="text-xs text-[#194a50]/40 whitespace-nowrap shrink-0 mt-0.5">
                                  {fmtTs(event.timestamp)}
                                </span>
                              </div>
                              {event.performed_by_email && (
                                <p className="text-xs text-[#194a50]/40 mt-0.5">
                                  {t('booking_detail.activity.by_label', { user: event.performed_by_email })}
                                  {event.performed_by_role && ` (${event.performed_by_role.replace('_', ' ')})`}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Price Breakdown */}
        <div className="space-y-6">
          {/* Price Breakdown - show for non-owners only */}
          {!isOwner && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <CreditCard className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.price.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-[#194a50]/70">{t('booking_detail.price.nights_rate', { n: price.nights, rate: price.nightly_rate })}</span>
                  <span className="text-[#0c2431]">€{price.subtotal?.toLocaleString(loc)}</span>
                </div>
                {price.cleaning_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#194a50]/70">{t('booking_detail.price.cleaning_fee')}</span>
                    <span className="text-[#0c2431]">€{price.cleaning_fee}</span>
                  </div>
                )}
                {price.check_in_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#194a50]/70">{t('booking_detail.price.checkin_fee')}</span>
                    <span className="text-[#0c2431]">€{price.check_in_fee}</span>
                  </div>
                )}
                {price.check_out_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#194a50]/70">{t('booking_detail.price.checkout_fee')}</span>
                    <span className="text-[#0c2431]">€{price.check_out_fee}</span>
                  </div>
                )}
                {price.stripe_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#194a50]/70">{t('booking_detail.price.stripe_fee')}</span>
                    <span className="text-[#0c2431]">€{price.stripe_fee?.toFixed(2)}</span>
                  </div>
                )}
                {price.discount_amount > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span>
                      {t('booking_detail.price.discount')}
                      {price.discount_type === 'percent' && ` (${price.discount_value}%)`}
                      {price.discount_reason ? ` — ${price.discount_reason}` : ''}
                    </span>
                    <span>−€{price.discount_amount?.toLocaleString(loc)}</span>
                  </div>
                )}
                {/* Totals — bruto first (what the guest is charged), then the
                    card-processing fee subtracted down to the net/owner basis
                    that owner invoices and payouts are built on. `cardFee` is
                    derived server-side and is display-only; when it is missing
                    (older API, or a role that doesn't get it) the card falls
                    back to showing the net total on its own, as before. */}
                <div className="border-t border-[#194a50]/10 pt-3 mt-3">
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-[#0c2431]">
                      {cardFee?.total_fee > 0
                        ? t('booking_detail.price.total_gross')
                        : t('booking_detail.price.total')}
                    </span>
                    <span className="text-[#194a50]">
                      {price.discount_amount > 0 && price.total_before_discount != null && (
                        <span className="mr-2 text-sm font-normal text-[#194a50]/40 line-through">€{price.total_before_discount?.toLocaleString(loc)}</span>
                      )}
                      €{(cardFee?.total_fee > 0 ? cardFee.gross_total : price.total_amount)?.toLocaleString(loc)}
                    </span>
                  </div>
                  {cardFee?.total_fee > 0 && (
                    <>
                      <div className="flex justify-between text-sm mt-2">
                        <span className="text-[#194a50]/70">
                          {t('booking_detail.price.card_fees', {
                            pct: (cardFee.rate * 100).toFixed(1),
                            fixed: cardFee.fixed.toFixed(2),
                          })}
                        </span>
                        <span className="text-[#0c2431]">−€{cardFee.total_fee?.toLocaleString(loc)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-semibold border-t border-[#194a50]/10 pt-2 mt-2">
                        <span className="text-[#0c2431]">{t('booking_detail.price.total_net')}</span>
                        <span className="text-[#194a50]">€{price.total_amount?.toLocaleString(loc)}</span>
                      </div>
                      <p className="mt-2 text-xs text-[#194a50]/50">{t('booking_detail.price.gross_note')}</p>
                    </>
                  )}
                </div>

                {/* Admin discount editor */}
                {isAdminOrCS && (
                  <div className="border-t border-[#194a50]/10 pt-3 mt-3 space-y-2">
                    <p className="text-sm font-semibold text-[#0c2431]">{t('booking_detail.price.discount_admin_title')}</p>
                    <p className="text-xs text-[#194a50]/60">{t('booking_detail.price.discount_admin_hint')}</p>
                    <div className="flex gap-2">
                      <select
                        className="border border-[#194a50]/20 rounded px-2 py-1.5 text-sm bg-white"
                        value={discountForm.discount_type}
                        onChange={(e) => setDiscountForm((f) => ({ ...f, discount_type: e.target.value }))}
                      >
                        <option value="amount">€</option>
                        <option value="percent">%</option>
                      </select>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="border border-[#194a50]/20 rounded px-2 py-1.5 text-sm w-24"
                        placeholder={discountForm.discount_type === 'percent' ? '10' : '150'}
                        value={discountForm.discount_value}
                        onChange={(e) => setDiscountForm((f) => ({ ...f, discount_value: e.target.value }))}
                      />
                      <input
                        type="text"
                        className="border border-[#194a50]/20 rounded px-2 py-1.5 text-sm flex-1"
                        placeholder={t('booking_detail.price.discount_reason')}
                        value={discountForm.discount_reason}
                        onChange={(e) => setDiscountForm((f) => ({ ...f, discount_reason: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleSaveDiscount(false)} disabled={savingDiscount || !discountForm.discount_value}>
                        {savingDiscount ? t('booking_detail.price.discount_saving') : t('booking_detail.price.discount_apply')}
                      </Button>
                      {price.discount_amount > 0 && (
                        <Button size="sm" variant="outline" onClick={() => handleSaveDiscount(true)} disabled={savingDiscount}>
                          {t('booking_detail.price.discount_remove')}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Payment Due Breakdown */}
                <div className="border-t border-[#194a50]/10 pt-3 mt-3 space-y-2">
                  <p className="text-sm font-semibold text-[#0c2431]">{t('booking_detail.price.payment_schedule')}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#194a50]/70">{(() => {
                      // Label must reflect the actual deposit basis the backend used:
                      // - Long-term (≥60 nights) -> 1 month's rent, not 20%.
                      // - Short-term at exactly 20% -> existing static label.
                      // - Short-term at a different % (custom property override) -> show the actual %.
                      const nights = booking?.nights || price.nights || 0;
                      const total = price.total_amount || 0;
                      const deposit = price.deposit_amount || 0;
                      if (nights >= 60) return t('booking_detail.price.booking_deposit_long_term');
                      if (total > 0) {
                        const pct = Math.round((deposit / total) * 100);
                        if (pct !== 20) return t('booking_detail.price.booking_deposit_actual_pct', { pct });
                      }
                      return t('booking_detail.price.booking_deposit_pct');
                    })()}</span>
                    <span className="text-[#0c2431]">€{price.deposit_amount?.toLocaleString(loc)}</span>
                  </div>
                  {price.security_deposit > 0 && (
                    <div className="flex justify-between text-sm bg-amber-50 p-2 rounded">
                      <span className="text-amber-800">{t('booking_detail.price.security_deposit')}</span>
                      <span className="font-medium text-amber-900">€{price.security_deposit?.toLocaleString(loc)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-[#194a50]/70">{t('booking_detail.price.balance_due')}</span>
                    <span className="text-[#0c2431]">€{(price.total_amount - price.deposit_amount)?.toLocaleString(loc)}</span>
                  </div>

                  {/* What the guest is actually charged per instalment, card fee
                      included. The rows above are net (owner basis). Months 2+
                      are grouped — they are identical apart from electricity,
                      which is only known once the tenant submits a meter read. */}
                  {cardFee?.long_term && cardFee.total_fee > 0 && (
                    <div className="mt-3 rounded bg-[#194a50]/5 p-2 space-y-1.5">
                      <p className="text-xs font-semibold text-[#0c2431]">{t('booking_detail.price.guest_charges')}</p>
                      {cardFeeCharge('deposit') && (
                        <div className="flex justify-between text-xs">
                          <span className="text-[#194a50]/70">{t('booking_detail.price.charge_deposit')}</span>
                          <span className="text-[#0c2431]">€{cardFeeCharge('deposit').gross?.toLocaleString(loc)}</span>
                        </div>
                      )}
                      {cardFeeCharge('month_1') && (
                        <div className="flex justify-between text-xs">
                          <span className="text-[#194a50]/70">{t('booking_detail.price.charge_month_1')}</span>
                          <span className="text-[#0c2431]">€{cardFeeCharge('month_1').gross?.toLocaleString(loc)}</span>
                        </div>
                      )}
                      {(() => {
                        const months = cardFee.charges.filter((c) => c.key.startsWith('month_') && c.key !== 'month_1');
                        if (!months.length) return null;
                        return (
                          <div className="flex justify-between text-xs">
                            <span className="text-[#194a50]/70">
                              {t('booking_detail.price.charge_months_rest', { from: 2, to: months.length + 1 })}
                            </span>
                            <span className="text-[#0c2431]">€{months[0].gross?.toLocaleString(loc)}</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Payment Summary - Shows actual payments */}
                {booking.payment_summary && (
                  <div className="border-t border-[#194a50]/10 pt-3 mt-3 space-y-2">
                    <p className="text-sm font-semibold text-[#0c2431]">{t('booking_detail.price.payment_summary')}</p>
                    {/* Total Paid is the NET contribution toward the booking
                        price, so it lines up with the Total above and with the
                        balance below. The gross charge is shown separately when
                        the two differ — they answer different questions, and
                        mixing them is what made the balance silently wrong. */}
                    <div className="flex justify-between text-sm">
                      <span className="text-[#194a50]/70">{t('booking_detail.price.total_paid')}</span>
                      <span className="text-green-600 font-medium">€{booking.payment_summary.total_paid?.toLocaleString(loc)}</span>
                    </div>
                    {booking.payment_summary.card_fee_paid > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-[#194a50]/50">{t('booking_detail.price.total_paid_gross')}</span>
                        <span className="text-[#194a50]/60">
                          €{booking.payment_summary.total_paid_gross?.toLocaleString(loc)}
                          <span className="ml-1 text-[#194a50]/40">
                            {t('booking_detail.price.incl_card_fee', { amount: booking.payment_summary.card_fee_paid?.toLocaleString(loc) })}
                          </span>
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-semibold">
                      <span className="text-[#194a50]">{t('booking_detail.price.balance_remaining')}</span>
                      <span className={booking.payment_summary.balance_remaining > 0 ? "text-amber-600" : "text-green-600"}>
                        €{booking.payment_summary.balance_remaining?.toLocaleString(loc)}
                      </span>
                    </div>
                    {booking.payment_summary.balance_due_date && booking.payment_summary.balance_remaining > 0 && (
                      <div className="text-xs text-[#194a50]/50">
                        {t('booking_detail.price.balance_due_by', { date: new Date(booking.payment_summary.balance_due_date).toLocaleDateString(loc) })}
                      </div>
                    )}
                    {booking.payment_summary.is_fully_paid && (
                      <div className="flex items-center gap-1 text-sm text-green-600 bg-green-50 p-2 rounded">
                        <CheckCircle2 className="w-4 h-4" />
                        {t('booking_detail.price.fully_paid')}
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Status */}
                <div className="border-t border-[#194a50]/10 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-[#0c2431]">{t('booking_detail.price.payment_status')}</span>
                    <Badge className={
                      booking.payment_status === 'BALANCE_PAID' ? 'bg-green-100 text-green-800' :
                      booking.payment_status === 'DEPOSIT_PAID' ? 'bg-blue-100 text-blue-800' :
                      'bg-amber-100 text-amber-800'
                    }>
                      {PAY_STATUS_LABELS[booking.payment_status] || booking.payment_status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Actions */}
          {!isOwner && !booking.is_owner_reservation && booking.payment_status !== 'BALANCE_PAID' && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <CreditCard className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.stripe.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {['DEPOSIT_DUE'].includes(booking.payment_status) && (
                  <Button
                    className="w-full bg-[#194a50] hover:bg-[#10555e]"
                    onClick={() => handleStripePayment('deposit')}
                    data-testid="pay-deposit-btn"
                  >
                    {t('booking_detail.stripe.pay_deposit', { amount: price.deposit_amount?.toLocaleString(loc) })}
                  </Button>
                )}
                {['DEPOSIT_PAID', 'BALANCE_DUE'].includes(booking.payment_status) && (
                  <Button
                    className="w-full bg-[#194a50] hover:bg-[#10555e]"
                    onClick={() => handleStripePayment('balance')}
                    data-testid="pay-balance-btn"
                  >
                    {t('booking_detail.stripe.pay_balance', { amount: (price.total_amount - price.deposit_amount)?.toLocaleString(loc) })}
                  </Button>
                )}
                <p className="text-xs text-[#194a50]/60 text-center">
                  {t('booking_detail.stripe.fees_note')}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Send Payment Reminder - Admin/CS only */}
          {isAdminOrCS && !booking.is_owner_reservation && booking.payment_status !== 'BALANCE_PAID' && (
            <Card className="shadow-sm border-amber-200 bg-amber-50">
              <CardContent className="py-4">
                <Button 
                  variant="outline"
                  className="w-full border-amber-300 text-amber-800 hover:bg-amber-100"
                  onClick={handleSendPaymentReminder}
                  disabled={sendingReminder}
                  data-testid="send-reminder-btn"
                >
                  {sendingReminder ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('booking_detail.reminder.sending')}
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      {t('booking_detail.reminder.send')}
                    </>
                  )}
                </Button>
                {booking.last_payment_reminder_sent && (
                  <p className="text-xs text-amber-700 mt-2 text-center">
                    {booking.payment_reminder_count
                      ? t('booking_detail.reminder.last_sent_count', { date: new Date(booking.last_payment_reminder_sent).toLocaleString(loc), n: booking.payment_reminder_count })
                      : t('booking_detail.reminder.last_sent', { date: new Date(booking.last_payment_reminder_sent).toLocaleString(loc) })}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Request balance / month-1 rent now - Admin/CS */}
          {isAdminOrCS && !booking.is_owner_reservation && ['DEPOSIT_PAID', 'BALANCE_DUE'].includes(booking.payment_status) && (
            <Card className="shadow-sm border-[#194a50]/20 bg-[#f5f0e8]">
              <CardContent className="py-4">
                <Button
                  variant="outline"
                  className="w-full border-[#194a50]/40 text-[#194a50] hover:bg-[#194a50]/10"
                  onClick={handleRequestBalance}
                  disabled={requestingBalance}
                  data-testid="request-balance-btn"
                >
                  {requestingBalance ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('booking_detail.reminder.requesting')}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {isLongTermBooking ? t('booking_detail.reminder.request_month1') : t('booking_detail.reminder.request_balance')}
                    </>
                  )}
                </Button>
                <p className="text-xs text-[#194a50]/70 mt-2 text-center">
                  {isLongTermBooking ? t('booking_detail.reminder.request_month1_hint') : t('booking_detail.reminder.request_balance_hint')}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Automatic reminders — Admin/CS */}
          {isAdminOrCS && !booking.is_owner_reservation && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <BellOff className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.reminders_pause.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="py-4 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span className={`text-sm font-medium ${booking.reminders_paused ? 'text-amber-700' : 'text-[#0c2431]'}`}>
                    {booking.reminders_paused
                      ? t('booking_detail.reminders_pause.paused')
                      : t('booking_detail.reminders_pause.on')}
                  </span>
                  <Switch
                    checked={!booking.reminders_paused}
                    disabled={pausingReminders}
                    onCheckedChange={(on) => handleToggleReminders(!on)}
                    data-testid="reminders-toggle"
                  />
                </div>
                <p className="text-xs text-[#194a50]/60">
                  {booking.reminders_paused
                    ? t('booking_detail.reminders_pause.hint_paused')
                    : t('booking_detail.reminders_pause.hint_on')}
                </p>
                {booking.reminders_paused && booking.reminders_paused_by_email && (
                  <p className="text-xs text-amber-700">
                    {t('booking_detail.reminders_pause.paused_by', {
                      email: booking.reminders_paused_by_email,
                      date: booking.reminders_paused_at
                        ? new Date(booking.reminders_paused_at).toLocaleDateString(loc)
                        : '—',
                    })}
                    {booking.reminders_pause_reason && ` — ${booking.reminders_pause_reason}`}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Signed Contract - Show for Admin/CS */}
          {isAdminOrCS && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <FileText className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.contract.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contractInfo ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">{t('booking_detail.contract.signed')}</span>
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-[#194a50]/60">{t('booking_detail.contract.signed_by')}</span>
                        <span className="font-medium text-[#0c2431]">{contractInfo.signature_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#194a50]/60">{t('booking_detail.contract.signed_on')}</span>
                        <span className="text-[#0c2431]">
                          {new Date(contractInfo.signed_at).toLocaleDateString(loc)}
                        </span>
                      </div>
                    </div>
                    <Button
                      className="w-full bg-[#194a50] hover:bg-[#10555e]"
                      onClick={handleDownloadContract}
                      disabled={downloadingContract}
                      data-testid="download-contract-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {downloadingContract ? t('booking_detail.contract.downloading') : t('booking_detail.contract.download')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-center py-2">
                      <FileText className="w-10 h-10 mx-auto mb-2 text-[#194a50]/30" />
                      <p className="text-sm text-[#194a50]/60">{t('booking_detail.contract.no_contract')}</p>
                    </div>
                    {/* Show Generate Contract button if deposit is paid */}
                    {['DEPOSIT_PAID', 'BALANCE_DUE', 'BALANCE_PAID'].includes(booking.payment_status) && (
                      <Button
                        className="w-full bg-[#194a50] hover:bg-[#10555e]"
                        onClick={handleGenerateContract}
                        disabled={generatingContract}
                        data-testid="generate-contract-btn"
                      >
                        {generatingContract ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('booking_detail.contract.generating')}
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4 mr-2" />
                            {t('booking_detail.contract.generate')}
                          </>
                        )}
                      </Button>
                    )}
                    {booking.payment_status === 'DEPOSIT_DUE' && (
                      <p className="text-xs text-amber-600 text-center">
                        <AlertCircle className="w-3 h-3 inline mr-1" />
                        {t('booking_detail.contract.wait_deposit')}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Card-fee passthrough (long-term monthly) - Admin/CS only */}
          {isAdminOrCS && isLongTermBooking && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <CreditCard className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.card_fee.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-[#194a50]/60 mb-2">{t('booking_detail.card_fee.help')}</p>
                <Select
                  value={
                    booking.pays_by_bank_transfer ? 'bank_transfer'
                      : booking.card_fee_passthrough == null ? 'auto'
                      : booking.card_fee_passthrough ? 'charge' : 'recover'
                  }
                  onValueChange={handleSetCardFee}
                  disabled={savingCardFee}
                >
                  <SelectTrigger data-testid="card-fee-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t('booking_detail.card_fee.auto')}</SelectItem>
                    <SelectItem value="charge">{t('booking_detail.card_fee.charge')}</SelectItem>
                    <SelectItem value="recover">{t('booking_detail.card_fee.dont_charge')}</SelectItem>
                    <SelectItem value="bank_transfer">{t('booking_detail.card_fee.bank_transfer')}</SelectItem>
                  </SelectContent>
                </Select>
                {booking.pays_by_bank_transfer && (
                  <p className="text-xs text-[#194a50]/60 mt-2">
                    {t('booking_detail.card_fee.bank_transfer_note')}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Electricity Tracking - Admin/CS only */}
          {isAdminOrCS && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <Zap className="w-5 h-5 text-amber-500" />
                  {t('booking_detail.electricity.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#194a50]/60">{t('booking_detail.electricity.checkin_kwh')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={t('booking_detail.electricity.enter_reading')}
                        value={checkInKwh}
                        onChange={(e) => setCheckInKwh(e.target.value)}
                        className="mt-1"
                        data-testid="check-in-kwh-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#194a50]/60">{t('booking_detail.electricity.checkout_kwh')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={t('booking_detail.electricity.enter_reading')}
                        value={checkOutKwh}
                        onChange={(e) => setCheckOutKwh(e.target.value)}
                        className="mt-1"
                        data-testid="check-out-kwh-input"
                      />
                    </div>
                  </div>
                  
                  {/* Show consumption if both values set */}
                  {booking.electricity_consumption && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                      <div className="flex justify-between mb-1">
                        <span className="text-amber-800">{t('booking_detail.electricity.consumption')}</span>
                        <span className="font-medium text-amber-900">
                          {t('booking_detail.electricity.consumption_value', { n: booking.electricity_consumption.consumption_kwh?.toFixed(2) })}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-amber-800">{t('booking_detail.electricity.cost_at_rate', { rate: booking.electricity_consumption.price_per_kwh })}</span>
                        <span className="font-semibold text-amber-900">
                          €{booking.electricity_consumption.total_cost?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}

                  <Button
                    className="w-full bg-[#194a50] hover:bg-[#10555e]"
                    onClick={handleSaveElectricity}
                    disabled={savingElectricity}
                    data-testid="save-electricity-btn"
                  >
                    {savingElectricity ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('booking_detail.electricity.saving')}
                      </>
                    ) : (
                      t('booking_detail.electricity.save_readings')
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Guest Refund (deposit - electricity) — Admin/CS only */}
          {isAdminOrCS && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <Banknote className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.refund.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Deductions editor (damage / broken items / other) */}
                  <div className="space-y-2">
                    <p className="text-xs text-[#194a50]/60">{t('booking_detail.refund.deductions_help')}</p>
                    {refundDeductions.map((d, i) => (
                      <div key={i} className="flex gap-2">
                        <Input
                          className="flex-1"
                          placeholder={t('booking_detail.refund.deduction_placeholder')}
                          value={d.description}
                          onChange={(e) => updateRefundDeduction(i, 'description', e.target.value)}
                        />
                        <Input
                          type="number" step="0.01" className="w-24"
                          placeholder="0.00"
                          value={d.amount}
                          onChange={(e) => updateRefundDeduction(i, 'amount', e.target.value)}
                        />
                        <button type="button" onClick={() => removeRefundDeduction(i)} className="text-red-400 hover:text-red-600 px-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addRefundDeduction} data-testid="add-refund-deduction">
                      <Plus className="w-4 h-4 mr-1" /> {t('booking_detail.refund.add_deduction')}
                    </Button>
                  </div>

                  {refundPreview && (
                    <div className="bg-[#e1dad2]/30 border border-[#194a50]/10 rounded-lg p-3 text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-[#194a50]/70">{t('booking_detail.refund.deposit_held')}</span>
                        <span className="font-medium text-[#0c2431]">€{Number(refundPreview.security_deposit || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#194a50]/70">{t('booking_detail.refund.electricity')}</span>
                        <span className="font-medium text-[#0c2431]">−€{Number(refundPreview.electricity?.total_cost || 0).toFixed(2)}</span>
                      </div>
                      {(refundPreview.deductions || []).map((d, i) => (
                        <div key={i} className="flex justify-between">
                          <span className="text-[#194a50]/70">{d.description || t('booking_detail.refund.deduction_placeholder')}</span>
                          <span className="font-medium text-[#0c2431]">−€{Number(d.amount || 0).toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between border-t border-[#194a50]/10 pt-1 mt-1">
                        <span className="font-semibold text-[#194a50]">{t('booking_detail.refund.amount_to_refund')}</span>
                        <span className="font-bold text-[#194a50]">€{Number(refundPreview.refund_amount || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={handlePreviewRefund}
                      disabled={loadingRefund}
                      data-testid="preview-refund-btn"
                    >
                      {loadingRefund ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      {t('booking_detail.refund.preview_button')}
                    </Button>
                    <Button
                      className="flex-1 bg-[#194a50] hover:bg-[#10555e]"
                      onClick={handleSendRefundNotice}
                      disabled={sendingRefund}
                      data-testid="send-refund-btn"
                    >
                      {sendingRefund ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      {t('booking_detail.refund.send_button')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Owner Simple Summary */}
          {isOwner && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-['Outfit']">
                  <Calendar className="w-5 h-5 text-[#194a50]" />
                  {t('booking_detail.owner_summary.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-center py-4">
                  <p className="text-4xl font-bold text-[#194a50]">{booking.nights}</p>
                  <p className="text-[#194a50]/70">{t('booking_detail.owner_summary.nights_reserved')}</p>
                </div>
                <div className="border-t border-[#194a50]/10 pt-3 text-center">
                  <p className="text-sm text-[#194a50]/60">
                    {new Date(booking.start_date).toLocaleDateString(loc)} - {new Date(booking.end_date).toLocaleDateString(loc)}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </div>

      {/* ── Payment Status Dialog ────────────────────────── */}
      {payStatusDialogOpen && pendingPayStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4" style={{
              background: pendingPayStatus === 'REFUNDED' ? '#dc2626'
                        : pendingPayStatus === 'BALANCE_PAID' ? '#166534'
                        : '#1d4ed8'
            }}>
              <h2 className="text-white text-xl font-bold font-['Outfit']">
                {t('booking_detail.dialog_pay_status.header_title', { label: PAY_STATUS_NEEDS_RECORD[pendingPayStatus]?.label })}
              </h2>
              <p className="text-white/70 text-sm mt-0.5">
                {t('booking_detail.dialog_pay_status.header_sub', { status: PAY_STATUS_LABELS[pendingPayStatus] || pendingPayStatus.replace(/_/g, ' ') })}
              </p>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Hint */}
              <div className="text-xs text-[#194a50]/60 bg-[#194a50]/5 rounded-lg px-3 py-2">
                {PAY_STATUS_NEEDS_RECORD[pendingPayStatus]?.hint(booking)}
              </div>

              {/* Amount */}
              <div>
                <Label className="text-sm font-medium text-[#0c2431] mb-1 block">
                  {t('booking_detail.dialog_pay_status.amount_label')} <span className="text-[#194a50]/40 font-normal">{t('booking_detail.dialog_pay_status.amount_hint')}</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={payStatusForm.amount}
                  onChange={(e) => setPayStatusForm({ ...payStatusForm, amount: e.target.value })}
                  className="text-lg font-semibold"
                  autoFocus
                />
              </div>

              {/* Payment method */}
              <div>
                <Label className="text-sm font-medium text-[#0c2431] mb-1 block">{t('booking_detail.dialog_pay_status.payment_method')}</Label>
                <Select value={payStatusForm.type} onValueChange={(v) => setPayStatusForm({ ...payStatusForm, type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BANK_TRANSFER">{t('booking_detail.dialog_pay_status.bank_transfer')}</SelectItem>
                    <SelectItem value="STRIPE_EU">{t('booking_detail.payments.stripe_eu')}</SelectItem>
                    <SelectItem value="STRIPE_INTL">{t('booking_detail.payments.stripe_intl')}</SelectItem>
                    <SelectItem value="CASH">{t('booking_detail.dialog_pay_status.cash')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-sm font-medium text-[#0c2431] mb-1 block">
                  {t('booking_detail.dialog_pay_status.reference_notes')} <span className="text-[#194a50]/40 font-normal">{t('booking_detail.dialog_pay_status.optional')}</span>
                </Label>
                <Input
                  placeholder={t('booking_detail.dialog_pay_status.reference_placeholder')}
                  value={payStatusForm.notes}
                  onChange={(e) => setPayStatusForm({ ...payStatusForm, notes: e.target.value })}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => { setPayStatusDialogOpen(false); setPendingPayStatus(null); }}
                disabled={savingPayStatus}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="flex-1 rounded-full text-white"
                style={{ background: pendingPayStatus === 'REFUNDED' ? '#dc2626' : pendingPayStatus === 'BALANCE_PAID' ? '#166534' : '#1d4ed8' }}
                onClick={handleConfirmPayStatusWithPayment}
                disabled={savingPayStatus}
              >
                {savingPayStatus
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('booking_detail.dialog_pay_status.saving')}</>
                  : payStatusForm.amount ? t('booking_detail.dialog_pay_status.record_update') : t('booking_detail.dialog_pay_status.update_only')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Booking Dialog (PENDING → CONFIRMED) ─── */}
      {confirmDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="bg-[#194a50] px-6 py-4">
              <h2 className="text-white text-xl font-bold font-['Outfit']">{t('booking_detail.dialog_confirm.title')}</h2>
              <p className="text-white/70 text-sm mt-0.5">
                {t('booking_detail.dialog_confirm.subtitle_changing', { from: t('booking_detail.status.pending'), to: t('booking_detail.status.confirmed') })}
              </p>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Deposit question */}
              <div>
                <Label className="text-sm font-semibold text-[#0c2431] mb-3 block">
                  {t('booking_detail.dialog_confirm.deposit_question')}
                </Label>
                <div className="flex gap-3">
                  {[
                    { value: 'yes', label: '✓ ' + t('booking_detail.dialog_confirm.yes_record'), cls: 'border-green-400 bg-green-50 text-green-800' },
                    { value: 'no',  label: '✕ ' + t('booking_detail.dialog_confirm.no_skip'),    cls: 'border-[#194a50]/30 bg-gray-50 text-gray-600' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setDepositRecorded(opt.value)}
                      className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        depositRecorded === opt.value ? opt.cls : 'border-gray-200 bg-white text-gray-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Deposit form — shown only when 'yes' */}
              {depositRecorded === 'yes' && (
                <div className="space-y-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div>
                    <Label className="text-xs text-green-800 mb-1 block">{t('booking_detail.dialog_confirm.deposit_amount')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder={t('booking_detail.dialog_confirm.deposit_example', { amount: (booking?.price_breakdown?.deposit_amount ?? ((booking?.price_breakdown?.total_amount || 0) * 0.2)).toFixed(0) })}
                      value={depositForm.amount}
                      onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
                      className="border-green-300 focus:border-green-500"
                    />
                    {booking?.price_breakdown?.total_amount && (
                      <p className="text-xs text-green-600 mt-1">
                        {t('booking_detail.dialog_confirm.deposit_hint_20', { amount: (booking.price_breakdown.deposit_amount ?? (booking.price_breakdown.total_amount * 0.2)).toFixed(2) })}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-green-800 mb-1 block">{t('booking_detail.dialog_confirm.payment_method')}</Label>
                    <Select value={depositForm.type} onValueChange={(v) => setDepositForm({ ...depositForm, type: v })}>
                      <SelectTrigger className="border-green-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BANK_TRANSFER">{t('booking_detail.dialog_pay_status.bank_transfer')}</SelectItem>
                        <SelectItem value="STRIPE_EU">{t('booking_detail.payments.stripe_eu')}</SelectItem>
                        <SelectItem value="STRIPE_INTL">{t('booking_detail.payments.stripe_intl')}</SelectItem>
                        <SelectItem value="CASH">{t('booking_detail.dialog_pay_status.cash')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-green-800 mb-1 block">{t('booking_detail.dialog_confirm.notes_ref')}</Label>
                    <Input
                      placeholder={t('booking_detail.dialog_confirm.notes_placeholder')}
                      value={depositForm.notes}
                      onChange={(e) => setDepositForm({ ...depositForm, notes: e.target.value })}
                      className="border-green-300"
                    />
                  </div>
                </div>
              )}

              {/* Info message */}
              <div className="flex gap-2 text-xs text-[#194a50]/60 bg-[#194a50]/5 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  {depositRecorded === 'yes'
                    ? t('booking_detail.dialog_confirm.info_recorded')
                    : t('booking_detail.dialog_confirm.info_skip')}
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => { setConfirmDialogOpen(false); setPendingStatusChange(null); }}
                disabled={confirmingStatus}
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="flex-1 rounded-full bg-[#194a50] hover:bg-[#10555e] text-white"
                onClick={handleConfirmStatusWithPayment}
                disabled={confirmingStatus || (depositRecorded === 'yes' && !depositForm.amount)}
              >
                {confirmingStatus ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('booking_detail.dialog_confirm.confirming')}</>
                ) : (
                  t('booking_detail.dialog_confirm.confirm_button')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Owner Invoice Dialog (unpaid only) ─── */}
      <Dialog open={editInvoiceOpen} onOpenChange={setEditInvoiceOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('booking_detail.invoice.edit_title', { number: editingInvoice?.invoice_number || '' })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-[#194a50]/60">{t('booking_detail.invoice.adjustments_long_help')}</p>

            {/* Existing line items */}
            {editAdjustments.length > 0 && (
              <div className="space-y-2">
                {editAdjustments.map((adj, i) => (
                  <div key={adj.id || i} className="flex items-center justify-between text-sm bg-[#e1dad2]/30 rounded px-3 py-2">
                    <span className="text-[#0c2431]">
                      {adj.description}
                      <span className={`ml-2 text-xs ${adj.type === 'deduction' ? 'text-red-600' : 'text-green-600'}`}>
                        ({adj.type === 'deduction' ? t('booking_detail.invoice.deduct_badge') : t('booking_detail.invoice.charge_badge')})
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className={adj.type === 'deduction' ? 'text-red-600' : 'text-green-600'}>
                        {adj.type === 'deduction' ? '-' : '+'}€{parseFloat(adj.amount).toFixed(2)}
                      </span>
                      <button type="button" onClick={() => setEditAdjustments(prev => prev.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Add a line item */}
            <div className="flex gap-2">
              <Input
                className="flex-1"
                placeholder={t('booking_detail.invoice.description_placeholder')}
                value={newEditAdj.description}
                onChange={(e) => setNewEditAdj({ ...newEditAdj, description: e.target.value })}
              />
              <Input
                type="number" step="0.01" className="w-24"
                placeholder="0.00"
                value={newEditAdj.amount}
                onChange={(e) => setNewEditAdj({ ...newEditAdj, amount: e.target.value })}
              />
              <Select value={newEditAdj.type} onValueChange={(val) => setNewEditAdj({ ...newEditAdj, type: val })}>
                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="charge">{t('booking_detail.invoice.charge')}</SelectItem>
                  <SelectItem value="deduction">{t('booking_detail.invoice.deduct')}</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={addEditAdjustment}><Plus className="w-4 h-4" /></Button>
            </div>

            <div>
              <Label>{t('booking_detail.invoice.invoice_notes_label')}</Label>
              <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder={t('booking_detail.invoice.invoice_notes_placeholder')} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditInvoiceOpen(false)}>{t('common.cancel')}</Button>
              <Button className="bg-[#194a50] hover:bg-[#10555e]" onClick={handleSaveInvoiceEdit} disabled={savingInvoiceEdit} data-testid="save-invoice-edit-btn">
                {savingInvoiceEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {t('common.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingDetailPage;
